// Global Instructions Rule Applied!

/**
 * Centralized API Routes Configuration
 *
 * This file contains all API routes used throughout the application,
 * organized by resource/module with their HTTP methods for type-safe API calls.
 */

/**
 * HTTP Methods Enum
 */
export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE'
}

/**
 * Route Definition Interface
 */
export interface RouteDefinition {
  path: string
  method: HttpMethod
  description?: string
}

/**
 * API Routes Configuration
 * Organized by resource/module for easy maintenance
 */
export const API_ROUTES = {
  // ============================================================================
  // AGENT ACCESS ROUTES  (Phase 3: Connect-agent consent + grant lifecycle)
  // ============================================================================
  INTERNAL_AGENTS: {
    SIGNALS: {
      path: '/internal-agents/signals',
      method: HttpMethod.GET,
      description: 'Agent signals (filter by context/status)'
    },
    SIGNAL_STATUS: {
      path: '/internal-agents/signals/:signalId/status',
      method: HttpMethod.POST,
      description: 'Set a signal status (acknowledged/dismissed/resolved/open)'
    },
    SIGNAL_CREATE_TASK: {
      path: '/internal-agents/signals/:signalId/create-task',
      method: HttpMethod.POST,
      description: 'Raise a task from a signal (human-triggered; agents never create tasks)'
    },
    DRAFTS: {
      path: '/internal-agents/drafts',
      method: HttpMethod.GET,
      description: 'Agent drafts for one account or prospect context (query: context_table, context_id, status)'
    },
    DRAFTS_MINE: {
      path: '/internal-agents/drafts/mine',
      method: HttpMethod.GET,
      description: "The caller's My Drafts inbox (assigned to them, or unassigned on an account they own)"
    },
    DRAFT_STATUS: {
      path: '/internal-agents/drafts/:draftId/status',
      method: HttpMethod.POST,
      description: 'Accept (copied), dismiss or reopen an agent draft — nothing is ever sent'
    },
    DRAFT_EXPORT_OUTLOOK: {
      path: '/internal-agents/drafts/:draftId/export-outlook',
      method: HttpMethod.POST,
      description: "Send to Outlook: create the agent draft in the caller's own Outlook Drafts folder (Graph, backend only) — still nothing is sent"
    },
    DRAFT_SENT_CANDIDATE: {
      path: '/internal-agents/drafts/:draftId/sent-candidate',
      method: HttpMethod.POST,
      description: "Confirm / Not this on the fuzzy sent candidate the sweep offered for a draft in Outlook (body {data:{accept}})"
    },
    DRAFT_MARK_SENT: {
      path: '/internal-agents/drafts/:draftId/mark-sent',
      method: HttpMethod.POST,
      description: "Mark a draft in Outlook as sent with an email already in Reservoir on its account (body {data:{interaction_id}})"
    },
    MEMORIES: {
      path: '/internal-agents/memories',
      method: HttpMethod.GET,
      description: 'Agent memories for an instance'
    },
    INSTANCES: {
      path: '/internal-agents/instances',
      method: HttpMethod.GET,
      description: 'Agent instances with counters'
    },
    RUNS: {
      path: '/internal-agents/runs',
      method: HttpMethod.GET,
      description: 'Agent run log for an instance'
    },
    BACKFILL: {
      path: '/internal-agents/backfill',
      method: HttpMethod.POST,
      description: 'Queue historical processing for a company/fund/deal or the fleet (admin)'
    },
    RUN_NOW: {
      path: '/internal-agents/run-now',
      method: HttpMethod.POST,
      description: 'Run one follower pass now for a single subject'
    },
    DEEPEN: {
      path: '/internal-agents/deepen',
      method: HttpMethod.POST,
      description: 'Queue the history behind a processed follower’s first backfill floor (admin)'
    },
    PROVISION: {
      path: '/internal-agents/provision',
      method: HttpMethod.POST,
      description: 'Create follower instances for an agent type (admin)'
    },
    ADMIN_RUNS: {
      path: '/internal-agents/admin/runs',
      method: HttpMethod.GET,
      description: 'Fleet run log with filters (admin)'
    },
    ADMIN_INSTANCES: {
      path: '/internal-agents/admin/instances',
      method: HttpMethod.GET,
      description: 'Windowed follower instance list for the Followers table (admin)'
    },
    ADMIN_QUEUE_SUMMARY: {
      path: '/internal-agents/admin/queue-summary',
      method: HttpMethod.GET,
      description: 'Queue depth, due-now pressure and cron tick state (admin)'
    },
    ADMIN_QUEUE_ACTIVE: {
      path: '/internal-agents/admin/queue/active',
      method: HttpMethod.POST,
      description: 'Pause or resume the agent queue tick (admin)'
    },
    ADMIN_QUEUE_CANCEL: {
      path: '/internal-agents/admin/queue/cancel',
      method: HttpMethod.POST,
      description: 'Trash queued agent runs (admin)'
    },
    ADMIN_QUEUE_RESTAGGER: {
      path: '/internal-agents/admin/queue/restagger',
      method: HttpMethod.POST,
      description: 'Re-space queued agent runs before a resume (admin)'
    },
    ADMIN_RUNS_RETRY: {
      path: '/internal-agents/admin/runs/retry',
      method: HttpMethod.POST,
      description: 'Re-queue a terminal (error) agent run as a fresh queued row (admin)'
    },
    COMPANY_BRIEF: {
      path: '/internal-agents/company-brief',
      method: HttpMethod.GET,
      description: 'Company Brief sections for an account'
    },
    COMPANY_BRIEF_CORRECT: {
      path: '/internal-agents/company-brief/correct',
      method: HttpMethod.POST,
      description: 'Owner correction — memory op + brief regeneration'
    },
    DEAL_BRIEF: {
      path: '/internal-agents/deal-brief',
      method: HttpMethod.GET,
      description: 'Deal Brief sections for a deal'
    },
    LP_BRIEF: {
      path: '/internal-agents/lp-brief',
      method: HttpMethod.GET,
      description: 'LP Brief sections for an LP account (relationship manager agent)'
    },
    LP_FUNDRAISING: {
      path: '/internal-agents/lp-fundraising',
      method: HttpMethod.GET,
      description: "An LP account's open fundraising processes with state and next step"
    },
    LP_FUNDRAISING_HISTORY: {
      path: '/internal-agents/lp-fundraising-history',
      method: HttpMethod.GET,
      description: "Every fundraising process an LP account has been in, with its outcome"
    },
    PROSPECT_BRIEF: {
      path: '/internal-agents/prospect-brief',
      method: HttpMethod.GET,
      description: 'Prospect Brief sections for a fundraising prospect (prospect tracker agent)'
    },
    INVESTMENT_CRITERIA: {
      path: '/internal-agents/investment-criteria',
      method: HttpMethod.GET,
      description: 'Investment criteria catalogue (deal evaluation rubric)'
    },
    INVESTMENT_CRITERIA_SAVE: {
      path: '/internal-agents/investment-criteria/save',
      method: HttpMethod.POST,
      description: 'Create/update an investment criterion (admin)'
    },
    INVESTMENT_CRITERIA_DELETE: {
      path: '/internal-agents/investment-criteria/delete',
      method: HttpMethod.POST,
      description: 'Soft-delete an investment criterion (admin)'
    },
    WRITTEN_CONTENT: {
      path: '/internal-agents/written-content',
      method: HttpMethod.GET,
      description: 'Union audit view of agent-written content'
    },
    COMPANY_STATES: {
      path: '/internal-agents/company-states',
      method: HttpMethod.GET,
      description: 'Current company states (Dashboard strip)'
    },
    COMPANY_STATES_OVERRIDE: {
      path: '/internal-agents/company-states/override',
      method: HttpMethod.POST,
      description: 'User state override (set_condition/edit_summary/close)'
    },
    COMPANY_STATE_CREATE_TASK: {
      path: '/internal-agents/company-states/:stateId/create-task',
      method: HttpMethod.POST,
      description: 'Raise a task from a company state card (human-triggered)'
    },
    DEAL_STATES: {
      path: '/internal-agents/deal-states',
      method: HttpMethod.GET,
      description: 'Current deal states (per-follower strips: Dashboard + Execution tab)'
    },
    DEAL_STATES_OVERRIDE: {
      path: '/internal-agents/deal-states/override',
      method: HttpMethod.POST,
      description: 'User deal-state override (set_condition/edit_summary/close)'
    },
    DEAL_STATE_CREATE_TASK: {
      path: '/internal-agents/deal-states/:stateId/create-task',
      method: HttpMethod.POST,
      description: 'Raise a task from a deal state card (human-triggered)'
    },
    PROSPECT_STATES: {
      path: '/internal-agents/prospect-states',
      method: HttpMethod.GET,
      description: 'Current prospect states (the prospect Dashboard strip)'
    },
    PROSPECT_STATES_OVERRIDE: {
      path: '/internal-agents/prospect-states/override',
      method: HttpMethod.POST,
      description: 'User prospect-state override (set_condition/edit_summary/close)'
    },
    PROSPECT_STATE_CREATE_TASK: {
      path: '/internal-agents/prospect-states/:stateId/create-task',
      method: HttpMethod.POST,
      description: 'Raise a task from a prospect state card (human-triggered)'
    },
    LP_STATES: {
      path: '/internal-agents/lp-states',
      method: HttpMethod.GET,
      description: 'Current LP account states (the account Dashboard strip)'
    },
    LP_STATES_OVERRIDE: {
      path: '/internal-agents/lp-states/override',
      method: HttpMethod.POST,
      description: 'User LP-state override (set_condition/edit_summary/close)'
    },
    LP_STATE_CREATE_TASK: {
      path: '/internal-agents/lp-states/:stateId/create-task',
      method: HttpMethod.POST,
      description: 'Raise a task from an LP state card (human-triggered)'
    },
    COMPANY_KPIS: {
      path: '/internal-agents/company-kpis',
      method: HttpMethod.GET,
      description: 'Company KPI page data (current, history, sparkline)'
    },
    DOCUSIGN_ENVELOPES: {
      path: '/internal-agents/docusign-envelopes',
      method: HttpMethod.GET,
      description: 'Detected DocuSign envelopes (manual-link surface)'
    },
    DOCUSIGN_ENVELOPES_LINK: {
      path: '/internal-agents/docusign-envelopes/link',
      method: HttpMethod.POST,
      description: 'Manually attach an unmatched envelope to a deal'
    },
    DOCUSIGN_ENVELOPES_DISMISS: {
      path: '/internal-agents/docusign-envelopes/dismiss',
      method: HttpMethod.POST,
      description: 'Dismiss an unmatched envelope (not deal-related)'
    },
    PIPELINE_MEETINGS: {
      path: '/internal-agents/pipeline-meetings',
      method: HttpMethod.POST,
      description: 'Pipeline meeting registry (paginated list)'
    },
    PIPELINE_MEETING_DETAIL: {
      path: '/internal-agents/pipeline-meetings/:meetingId',
      method: HttpMethod.GET,
      description: 'One pipeline meeting: minutes, per-deal notes, transcript'
    },
    PIPELINE_MEETING_THEMES: {
      path: '/internal-agents/pipeline-meetings/themes',
      method: HttpMethod.GET,
      description: 'Series-current investment themes doc'
    },
    PIPELINE_MEETING_REPROCESS: {
      path: '/internal-agents/pipeline-meetings/:meetingId/reprocess',
      method: HttpMethod.POST,
      description: 'Admin: re-enqueue a meeting segmentation/minutes run'
    },
    PIPELINE_MEETING_FOLLOW_UPS: {
      path: '/internal-agents/pipeline-meetings/follow-ups',
      method: HttpMethod.POST,
      description: "One meeting's follow-ups (shown beneath its minutes)"
    },
    PIPELINE_MEETING_FOLLOW_UP_STATUS: {
      path: '/internal-agents/pipeline-meetings/follow-ups/:followUpId/status',
      method: HttpMethod.POST,
      description: 'Tick a follow-up done or reopen it (open | done)'
    },
    PIPELINE_MEETING_FOLLOW_UP_CREATE_TASK: {
      path: '/internal-agents/pipeline-meetings/follow-ups/:followUpId/create-task',
      method: HttpMethod.POST,
      description: 'Raise a task from a follow-up (human-triggered; picked deal + its company linked server-side)'
    },
    PIPELINE_MEETING_FOLLOW_UP_ADD: {
      path: '/internal-agents/pipeline-meetings/follow-ups/add',
      method: HttpMethod.POST,
      description: 'A person adds a follow-up to a meeting'
    },
    PIPELINE_MEETING_FOLLOW_UP_EDIT: {
      path: '/internal-agents/pipeline-meetings/follow-ups/:followUpId/edit',
      method: HttpMethod.POST,
      description: 'A person rewrites a follow-up (wording / owner / due date / deal; original kept, editor stamped)'
    },
    PIPELINE_MEETING_FOLLOW_UP_REMOVE: {
      path: '/internal-agents/pipeline-meetings/follow-ups/:followUpId/remove',
      method: HttpMethod.POST,
      description: 'A person removes a follow-up (soft delete; a linked task stays)'
    },
    PIPELINE_MEETING_MINUTES_EDIT: {
      path: '/internal-agents/pipeline-meetings/:meetingId/minutes',
      method: HttpMethod.POST,
      description: "A person's edit of the minutes — a new version, editor stamped"
    },
    PIPELINE_MEETING_MINUTES_RESTORE: {
      path: '/internal-agents/pipeline-meetings/:meetingId/minutes/restore',
      method: HttpMethod.POST,
      description: 'Make an earlier minutes version current again (as a new version)'
    },
    PIPELINE_MEETING_MINUTES_HISTORY: {
      path: '/internal-agents/pipeline-meetings/:meetingId/minutes/history',
      method: HttpMethod.GET,
      description: 'Every minutes version of a meeting, newest first'
    }
  },
  MEETING_TRANSCRIPTS: {
    WATCHES: {
      path: '/meeting-transcripts/watches',
      method: HttpMethod.GET,
      description: 'Meeting watches (admin, Configuration → Interaction Tracking)'
    },
    WATCH_SAVE: {
      path: '/meeting-transcripts/watches/save',
      method: HttpMethod.POST,
      description: 'Create/update a meeting watch (admin)'
    },
    WATCH_DELETE: {
      path: '/meeting-transcripts/watches/delete',
      method: HttpMethod.POST,
      description: 'Soft-delete a meeting watch (admin)'
    },
    WATCH_RESOLVE: {
      path: '/meeting-transcripts/watches/resolve',
      method: HttpMethod.POST,
      description: 'Resolve a watch join link to its Graph onlineMeeting id (admin)'
    }
  },
  AGENT_ACCESS: {
    GRANTS_LIST: {
      path: '/agent/grants',
      method: HttpMethod.GET,
      description: 'My agent grants (active + revoked history)'
    },
    GRANTS_UPDATE: {
      path: '/agent/grants/:grantId/update',
      method: HttpMethod.POST,
      description:
        'Edit a grant in place (tools, per-write-tool mode, expiry). Editing an expired grant renews it'
    },
    GRANTS_REVOKE: {
      path: '/agent/grants/:grantId',
      method: HttpMethod.DELETE,
      description: 'Revoke a grant (per-user kill switch; takes effect within the token TTL)'
    },
    PRODUCTS_LIST: {
      path: '/agent/products',
      method: HttpMethod.GET,
      description: 'Agent products (admin tab): every live cfg.agents row, pending DCR quarantines first'
    },
    PRODUCTS_SET_ENABLED: {
      path: '/agent/products/enabled',
      method: HttpMethod.POST,
      description: 'Approve (enable) or kill-switch (disable) an agent product'
    },
    PROPOSALS_LIST: {
      path: '/agent/proposals',
      method: HttpMethod.GET,
      description: 'My agent proposals (Phase 4 propose-approve), pending first'
    },
    PROPOSALS_REVIEW: {
      path: '/agent/proposals/:proposalId/review',
      method: HttpMethod.POST,
      description: 'Approve (replay through the real write path) or reject (→ trashed) one pending proposal'
    },
    PROPOSALS_RETRY: {
      path: '/agent/proposals/:proposalId/retry',
      method: HttpMethod.POST,
      description: 'Re-approve one FAILED proposal: the same replay as approve, on the already-reviewed payload'
    },
    PROPOSALS_UNDO: {
      path: '/agent/proposals/:proposalId/undo',
      method: HttpMethod.POST,
      description: 'Undo an applied agent write (live or approved) — trashes the created records'
    },
    ACTIVITY_SUMMARY: {
      path: '/agent/activity/summary',
      method: HttpMethod.GET,
      description: 'My agent activity stat strip: reads/rows today, writes this week, pending review'
    }
  },

  // ============================================================================
  // HELP ROUTES
  // ============================================================================
  // The in-app "?" guide drawer. Content lives in cfg.help_articles, so a
  // reworded guide is a migration rather than a frontend release. Fetched
  // lazily per article (and cached for the session) instead of riding the
  // global-configs payload, which already carries ~50 cfg tables and is
  // re-pulled on every config-version bump.
  HELP: {
    ARTICLE: {
      path: '/help/articles/:articleKey',
      method: HttpMethod.GET,
      description: 'One help article (title, subtitle, ordered sections) by slug; 404 when unwritten'
    }
  },

  // ============================================================================
  // COMPANIES ROUTES
  // ============================================================================
  COMPANIES: {
    LIST: { path: '/accounts', method: HttpMethod.GET, description: 'List companies with pagination/filters' },
    GET: { path: '/accounts/:id', method: HttpMethod.GET, description: 'Get single company by ID' },
    DELETE: { path: '/accounts/:id', method: HttpMethod.DELETE, description: 'Soft delete company (account)' },
    PATCH: { path: '/accounts', method: HttpMethod.PATCH, description: 'Patch account columns (body {id, <column>: value}) — the InputManager autosave route' },
    UPDATE: { path: '/accounts/:id', method: HttpMethod.PUT, description: 'Update company' }, // TODO: We don't need this as we will use PATCH
    FILTER_OPTIONS: {
      path: '/accounts/filter-options',
      method: HttpMethod.POST,
      description: 'Get filter options for companies'
    },
    FILTER: { path: '/accounts/filter', method: HttpMethod.POST, description: 'Advanced filtering for companies' },
    MOBILE_LIST: { path: '/accounts/mobile-list', method: HttpMethod.POST, description: 'Lean mobile companies list (id/name/stats)' },
    SEARCH: { path: '/accounts/search', method: HttpMethod.GET, description: 'Search companies for typeahead' },
    SOURCING: {
      path: '/accounts/:id/sourcing',
      method: HttpMethod.GET,
      description: 'Get sourcing contacts for a company'
    },
    DOCUMENTS: {
      path: '/accounts/:id/documents',
      method: HttpMethod.GET,
      description: 'Company documents: cfg category/type catalogue + the account files'
    },
    DOCUMENTS_CLASSIFY: {
      path: '/accounts/documents/classify',
      method: HttpMethod.PUT,
      description: "Set a company file's document type"
    },
    HOLDINGS_DATA: {
      path: '/accounts/:id/holdings-data',
      method: HttpMethod.GET,
      description: 'Get company holdings data (holdings, transaction summary, transaction detail)'
    },
    COMMITMENTS_FILTER: {
      path: '/accounts/:id/commitments/filter',
      method: HttpMethod.POST,
      description: "Get an account's fund commitments (Commitments tab, filtered/sorted/paginated)"
    },
    SHORT_DESCRIPTION: {
      path: '/company-short-descriptions/:id',
      method: HttpMethod.GET,
      description: "A company's short description for reports, with who wrote it and its sign-off"
    },
    SHORT_DESCRIPTION_PATCH: {
      path: '/company-short-descriptions',
      method: HttpMethod.PATCH,
      description: 'A person edits (or clears) the short description (InputManager auto-patch: { id, short_description })'
    },
    SHORT_DESCRIPTION_SIGN_OFF: {
      path: '/company-short-descriptions/:id/sign-off',
      method: HttpMethod.POST,
      description: "Sign off the agent-written short description; answers with the company's short description"
    },
    SHORT_DESCRIPTIONS_STATUS: {
      path: '/company-short-descriptions/status',
      method: HttpMethod.GET,
      description: 'Admins: how the portfolio stands - written, signed off, waiting for a sign-off, to generate, nothing to write from, declined'
    },
    SHORT_DESCRIPTIONS_GENERATE: {
      path: '/company-short-descriptions/generate',
      method: HttpMethod.POST,
      description: 'Admins: the agent writes the next batch of missing short descriptions (data.include_declined retries the ones it declined); answers with the batch and how many remain'
    },
    SHORT_DESCRIPTIONS_AGENT_WRITTEN: {
      path: '/company-short-descriptions/agent-written',
      method: HttpMethod.GET,
      description: 'Admins: every agent-written short description, unreviewed first (the bulk review list)'
    },
    SHORT_DESCRIPTIONS_SIGN_OFF: {
      path: '/company-short-descriptions/sign-off',
      method: HttpMethod.POST,
      description: "Admins: sign off the agent's unreviewed texts among data.account_ids; answers with how many"
    },
    SHORT_DESCRIPTIONS_REGENERATE: {
      path: '/company-short-descriptions/regenerate',
      method: HttpMethod.POST,
      description: "Admins: the agent writes data.account_ids' texts again (at most 25 a request; never over a person's text); answers in the Generate batch shape"
    }
  },

  // ============================================================================
  // CONTACTS ROUTES
  // ============================================================================
  CONTACTS: {
    LIST: { path: '/contacts', method: HttpMethod.GET, description: 'List contacts with pagination/filters' },
    GET: { path: '/contacts/:id', method: HttpMethod.GET, description: 'Get single contact by ID' },
    DELETE: { path: '/contacts/:id', method: HttpMethod.DELETE, description: 'Soft delete contact' },
    CREATE: { path: '/contacts', method: HttpMethod.POST, description: 'Create new contact' },
    PATCH: { path: '/contacts', method: HttpMethod.PATCH, description: 'Patch contact record' },
    FILTER: { path: '/contacts/filter', method: HttpMethod.POST, description: 'Advanced filtering for contacts' },
    FILTER_OPTIONS: {
      path: '/contacts/filter-options',
      method: HttpMethod.POST,
      description: 'Get filter options for contacts'
    },
    COMPANY_SEARCH: {
      path: '/contacts/company-search',
      method: HttpMethod.GET,
      description: 'Search companies for contact relationships'
    },
    VALIDATE_EMAIL: {
      path: '/contacts/validate-email',
      method: HttpMethod.GET,
      description: 'Validate email address uniqueness'
    },
    SEARCH: { path: '/contacts/search', method: HttpMethod.GET, description: 'Search contacts for typeahead' }
  },

  // ============================================================================
  // FUNDRAISING ROUTES
  // ============================================================================
  FUNDRAISING: {
    LIST: { path: '/prospects', method: HttpMethod.GET, description: 'List prospects with pagination/filters' },
    GET: { path: '/prospects/:id', method: HttpMethod.GET, description: 'Get single prospect by ID' },
    DELETE: { path: '/prospects/:id', method: HttpMethod.DELETE, description: 'Soft delete prospect' },
    CREATE: { path: '/prospects', method: HttpMethod.POST, description: 'Create new prospect' },
    PATCH: { path: '/prospects', method: HttpMethod.PATCH, description: 'Patch prospect record' },
    UPDATE: { path: '/prospects/:id', method: HttpMethod.PUT, description: 'Update prospect' },
    FILTER_OPTIONS: {
      path: '/prospects/filter-options',
      method: HttpMethod.POST,
      description: 'Get filter options for prospects'
    },
    FILTER: { path: '/prospects/filter', method: HttpMethod.POST, description: 'Advanced filtering for prospects' },
    SEARCH: { path: '/prospects/search', method: HttpMethod.GET, description: 'Search prospects for typeahead' },
    PIPELINE_PROSPECTS: {
      path: '/pipeline-prospects',
      method: HttpMethod.PATCH,
      description: 'Patch pipeline_prospects record'
    },
    BULK_CANDIDATES: {
      path: '/prospects/bulk-candidates',
      method: HttpMethod.POST,
      description: 'Resolve candidate LP accounts for bulk prospect creation'
    },
    BULK_CREATE: {
      path: '/prospects/bulk-create',
      method: HttpMethod.POST,
      description: 'Atomically create prospects for many LP accounts in one fund'
    }
  },

  // ============================================================================
  // DEALS ROUTES
  // ============================================================================
  DEALS: {
    LIST: { path: '/deals', method: HttpMethod.GET, description: 'List deals with pagination/filters' },
    GET: { path: '/deals/:id', method: HttpMethod.GET, description: 'Get single deal by ID' },
    DELETE: { path: '/deals/:id', method: HttpMethod.DELETE, description: 'Soft delete deal' },
    CREATE: { path: '/deals', method: HttpMethod.POST, description: 'Create new deal' },
    PATCH: { path: '/deals', method: HttpMethod.PATCH, description: 'Patch deal record' },
    UPDATE: { path: '/deals/:id', method: HttpMethod.PUT, description: 'Update deal' }, // TODO: We don't need this as we will use PATCH
    FILTER: { path: '/deals/filter', method: HttpMethod.POST, description: 'Advanced filtering for deals' },
    MOBILE_LIST: { path: '/deals/mobile-list', method: HttpMethod.POST, description: 'Lean mobile deals list (id/name/company/stage + Val/raise/commitment)' },
    FILTER_OPTIONS: {
      path: '/deals/filter-options',
      method: HttpMethod.POST,
      description: 'Get filter options for deals'
    },
    SEARCH: { path: '/deals/search', method: HttpMethod.GET, description: 'Search deals for typeahead' },
    PIPELINE: { path: '/deals/pipeline', method: HttpMethod.GET, description: 'Get pipeline-specific deals' },
    PIPELINE_BUDGET: {
      path: '/deals/pipeline-budget',
      method: HttpMethod.POST,
      description: 'Deal Pipeline Budget tab: capital + cash position of the followed funds and their open deal commitments'
    },
    COMMITMENTS: { path: '/deals/:id/commitments', method: HttpMethod.PUT, description: 'Update deal commitments' }
  },

  // ============================================================================
  // DEAL TASKS ROUTES
  // ============================================================================
  DEAL_TASKS: {
    LIST: { path: '/deals/:dealId/tasks', method: HttpMethod.GET, description: 'Get tasks for a deal' }
  },

  // ============================================================================
  // DEAL INTERACTIONS ROUTES
  // ============================================================================
  DEAL_INTERACTIONS: {
    LIST: {
      path: '/deals/:dealId/interactions',
      method: HttpMethod.POST,
      description: 'Get interactions for a deal with filters'
    }
  },

  // ============================================================================
  // ACCOUNT TASKS ROUTES
  // ============================================================================
  ACCOUNT_TASKS: {
    LIST: { path: '/accounts/:accountId/tasks', method: HttpMethod.GET, description: 'Get tasks for an account' }
  },

  // ============================================================================
  // PROSPECT TASKS ROUTES
  // ============================================================================
  PROSPECT_TASKS: {
    LIST: { path: '/prospects/:prospectId/tasks', method: HttpMethod.GET, description: 'Get tasks for a prospect' }
  },

  // ============================================================================
  // ACCOUNT INTERACTIONS ROUTES
  // ============================================================================
  ACCOUNT_INTERACTIONS: {
    LIST: {
      path: '/accounts/:accountId/interactions',
      method: HttpMethod.POST,
      description: 'Get interactions for an account with filters'
    }
  },

  // ============================================================================
  // PROSPECT INTERACTIONS ROUTES
  // ============================================================================
  PROSPECT_INTERACTIONS: {
    LIST: {
      path: '/prospects/:prospectId/interactions',
      method: HttpMethod.POST,
      description: 'Get interactions for a prospect with filters'
    }
  },

  // ============================================================================
  // INTERACTIONS ROUTES (Global)
  // ============================================================================
  INTERACTIONS: {
    CREATE: { path: '/interactions', method: HttpMethod.POST, description: 'Create interaction globally' },
    UPDATE: {
      path: '/interactions/:interactionId',
      method: HttpMethod.PUT,
      description: 'Update interaction globally'
    },
    DELETE: {
      path: '/interactions/:interactionId',
      method: HttpMethod.DELETE,
      description: 'Delete interaction globally'
    },
    DELINK: {
      path: '/interactions/:interactionId/delink',
      method: HttpMethod.POST,
      description: 'Delink interaction from module (hard delete relationship)'
    },
    LINKS: {
      path: '/interactions/:interactionId/links',
      method: HttpMethod.GET,
      description: 'Get interaction links (accounts, deals, prospects)'
    },
    PROCESS: {
      path: '/interactions/process',
      method: HttpMethod.POST,
      description: 'Process interaction links (update accounts, deals, prospects)'
    }
  },

  // ============================================================================
  // PIPELINE DEALS ROUTES
  // ============================================================================
  PIPELINE_DEALS: {
    PATCH: { path: '/pipeline-deals', method: HttpMethod.PATCH, description: 'Patch pipeline_deals record' }
  },

  ROUNDS: {
    PATCH: { path: '/rounds', method: HttpMethod.PATCH, description: 'Patch a rounds record (bare columns, incl. id)' },
    CREATE: { path: '/rounds', method: HttpMethod.POST, description: 'Create a standalone round for a company' },
    DETAIL: { path: '/rounds/:id', method: HttpMethod.GET, description: 'One round (RoundEntry shape) — full-form cold load' },
    DELETE_IMPACT: { path: '/rounds/:id/delete-impact', method: HttpMethod.GET, description: 'What deleting this round would touch, and whether it is blocked' },
    DELETE: { path: '/rounds/:id', method: HttpMethod.DELETE, description: 'Soft delete a round: cascades instruments/participations, unlinks deals (422 while a cap-table version or legal set references it)' }
  },

  // ============================================================================
  // DEAL EXECUTION ROUTES
  // ============================================================================
  DEAL_EXECUTION: {
    BOARD: { path: '/deal-execution/board', method: HttpMethod.POST, description: 'Merged execution board rows' },
    CHECKLIST_ANSWER: {
      path: '/deal-execution/checklist-answer',
      method: HttpMethod.POST,
      description: 'Set one stage checklist answer'
    },
    STAGE_HISTORY: {
      path: '/deal-execution/stage-history',
      method: HttpMethod.GET,
      description: 'Execution entry provenance + stage transitions'
    },
    ENTER: {
      path: '/deal-execution/enter',
      method: HttpMethod.POST,
      description: 'Manual promotion into the execution pipeline'
    },
    MOVE_STAGE: {
      path: '/deal-execution/move-stage',
      method: HttpMethod.POST,
      description: 'Next/Back stage move — lands the deal at the top of the target column'
    },
    CONTEXT: {
      path: '/deal-execution/context',
      method: HttpMethod.GET,
      description: 'Round summary + linked asset transactions for the Execution tab'
    },
    WIRE_GET: {
      path: '/deal-execution/wire',
      method: HttpMethod.GET,
      description: 'Wire instructions + auto-fill suggestion (cfg email-gated)'
    },
    WIRE_SAVE: {
      path: '/deal-execution/wire',
      method: HttpMethod.PUT,
      description: 'Full-row upsert of the deal’s wire instructions'
    },
    DOCUMENTS: {
      path: '/deal-execution/documents',
      method: HttpMethod.GET,
      description: 'Deal files with classification + applicable requirement set'
    },
    DOCUMENTS_CLASSIFY: {
      path: '/deal-execution/documents/classify',
      method: HttpMethod.PUT,
      description: 'Set a file’s document type / lifecycle / version'
    },
    ACCOUNT_OVERVIEW: {
      path: '/deal-execution/account-overview',
      method: HttpMethod.GET,
      description: 'Account form Deals & Rounds tab: both tables in one read'
    },
    ROUND_INSTRUMENT_SAVE: {
      path: '/deal-execution/round-instrument',
      method: HttpMethod.POST,
      description: 'Insert/update a secondary or converting instrument on the round'
    },
    ROUND_INSTRUMENT_DELETE: {
      path: '/deal-execution/round-instrument',
      method: HttpMethod.DELETE,
      description: 'Soft-delete a round instrument'
    },
    ROUND_PARTICIPATION_SAVE: {
      path: '/deal-execution/round-participation',
      method: HttpMethod.POST,
      description: 'Insert/update a co-investor participation on the round'
    },
    ROUND_PARTICIPATION_DELETE: {
      path: '/deal-execution/round-participation',
      method: HttpMethod.DELETE,
      description: 'Soft-delete a round participation'
    },
    CAP_TABLE_GET: {
      path: '/deal-execution/cap-table',
      method: HttpMethod.GET,
      description: 'Cap-table view: history, snapshot, tiles, lines, holders'
    },
    CAP_TABLE_SAVE: {
      path: '/deal-execution/cap-table',
      method: HttpMethod.PUT,
      description: 'Create/update a draft cap-table version (header + lines + holders)'
    },
    CAP_TABLE_SET_CURRENT: {
      path: '/deal-execution/cap-table/set-current',
      method: HttpMethod.POST,
      description: 'Promote a cap-table version to current (supersedes the prior current)'
    },
    CAP_TABLE_SPLIT: {
      path: '/deal-execution/cap-table/split',
      method: HttpMethod.POST,
      description: 'Record a share split: clone current snapshot restated by a factor'
    },
    CAP_TABLE_DELETE: {
      path: '/deal-execution/cap-table',
      method: HttpMethod.DELETE,
      description: 'Soft-delete a non-current cap-table version'
    },
    CO_INVESTMENTS: {
      path: '/deal-execution/co-investments',
      method: HttpMethod.GET,
      description: "Account's holdings across cap tables, latest per company"
    },
    LEGAL_RIGHTS_GET: {
      path: '/deal-execution/legal-rights',
      method: HttpMethod.GET,
      description: 'Legal-rights view: agreement sets, evaluated rights, position, pickers'
    },
    LEGAL_SET_SAVE: {
      path: '/deal-execution/legal-set',
      method: HttpMethod.PUT,
      description: 'Create/update an agreement set (header + full rights list)'
    },
    LEGAL_SET_STATUS: {
      path: '/deal-execution/legal-set/status',
      method: HttpMethod.POST,
      description: 'Execute (with supersedes list) or revert an agreement set'
    },
    LEGAL_SET_DELETE: {
      path: '/deal-execution/legal-set',
      method: HttpMethod.DELETE,
      description: 'Soft-delete an agreement set and its rights'
    }
  },

  // ============================================================================
  // STAGE ALERTS ROUTES (Phase 6 — stage-aging bell + per-pipeline recipients)
  // ============================================================================
  STAGE_ALERTS: {
    GET: {
      path: '/stage-alerts',
      method: HttpMethod.GET,
      description: "Active stage-aging alerts visible to the signed-in user's email + unread count"
    },
    MARK_READ: {
      path: '/stage-alerts/mark-read',
      method: HttpMethod.POST,
      description: 'Mark specific alerts (alert_ids) or all visible alerts (omit) as read'
    },
    RECIPIENTS_SET: {
      path: '/stage-alerts/recipients/:pipelineId',
      method: HttpMethod.PUT,
      description: "Full-replace a pipeline's stage-alert recipient email list (cfg)"
    },
    DIGEST_SCHEDULE_GET: {
      path: '/stage-alerts/digest-schedule',
      method: HttpMethod.GET,
      description: 'Digest send schedule (cfg single row) + next send dates'
    },
    DIGEST_SCHEDULE_SET: {
      path: '/stage-alerts/digest-schedule',
      method: HttpMethod.PUT,
      description: 'Save the digest send schedule (every N days / weekly / monthly)'
    }
  },

  // ============================================================================
  // PIPELINES ROUTES
  // ============================================================================
  PIPELINES: {
    GET: { path: '/pipelines', method: HttpMethod.GET, description: 'Get all pipelines' }
  },

  // ============================================================================
  // PIPELINE CONFIGS ROUTES
  // ============================================================================
  PIPELINE_CONFIGS: {
    GET: { path: '/pipeline-configs', method: HttpMethod.GET, description: 'Get all pipeline configs' }
  },

  // ============================================================================
  // TASKS ROUTES (Global)
  // ============================================================================
  TASKS: {
    CREATE: { path: '/tasks', method: HttpMethod.POST, description: 'Create new task' },
    UPDATE: { path: '/tasks/:id', method: HttpMethod.PUT, description: 'Update task globally' },
    DELETE: { path: '/tasks/:id', method: HttpMethod.DELETE, description: 'Delete task globally' },
    REORDER: { path: '/tasks/reorder', method: HttpMethod.POST, description: 'Reorder tasks globally' },
    LINKS: { path: '/tasks/:taskId/links', method: HttpMethod.GET, description: 'Get task links (accounts, deals, prospects)' },
    PROCESS: { path: '/tasks/process', method: HttpMethod.POST, description: 'Process task links (update accounts, deals, prospects)' }
  },

  // ============================================================================
  // FUNDS ROUTES
  // ============================================================================
  FUNDS: {
    LIST: { path: '/funds', method: HttpMethod.GET, description: 'List all funds (tablegrid)' },
    FILTER: { path: '/funds/filter', method: HttpMethod.POST, description: 'Filtered/sorted/paginated funds tablegrid' },
    MOBILE_LIST: { path: '/funds/mobile-list', method: HttpMethod.POST, description: 'Lean mobile funds list (id/name/closed/FMV/MOIC)' },
    FILTER_OPTIONS: { path: '/funds/filter-options', method: HttpMethod.GET, description: 'Filter option lists for the funds tablegrid' },
    GET: { path: '/funds/:id', method: HttpMethod.GET, description: 'Fund dashboard detail (single fund)' },
    INVESTMENTS: { path: '/funds/:id/investments', method: HttpMethod.GET, description: 'Investments by company for one fund' },
    INVESTMENTS_FILTER: { path: '/funds/:id/investments/filter', method: HttpMethod.POST, description: 'Filtered/sorted/paginated investments by company' },
    INVESTMENTS_FILTER_OPTIONS: { path: '/funds/:id/investments/filter-options', method: HttpMethod.GET, description: 'Filter option lists for investments by company (industries etc.)' },
    INVESTMENT_DETAIL_FILTER: { path: '/funds/:id/investment-detail/filter', method: HttpMethod.POST, description: 'Configurable per-fund investment detail grid (user-selected aggregation)' },
    INVESTMENT_DETAIL_FILTER_OPTIONS: { path: '/funds/:id/investment-detail/filter-options', method: HttpMethod.GET, description: 'Filter option lists for the investment detail grid' },
    STANDALONE_INVESTMENT_DETAIL_FILTER: { path: '/funds/investment-detail/filter', method: HttpMethod.POST, description: 'Standalone multi-fund Investment Detail grid (Investments screen)' },
    STANDALONE_INVESTMENT_DETAIL_FILTER_OPTIONS: { path: '/funds/investment-detail/filter-options', method: HttpMethod.POST, description: 'Filter option lists for the standalone Investment Detail grid' },
    INVESTORS_SUMMARY: { path: '/funds/:id/investors/summary', method: HttpMethod.GET, description: 'Investors-tab summary (committed capital + investor count)' },
    INVESTORS_FILTER: { path: '/funds/:id/investors/filter', method: HttpMethod.POST, description: 'Per-fund investors grid (one row per fund_commitment)' },
    INVESTORS_FILTER_OPTIONS: { path: '/funds/:id/investors/filter-options', method: HttpMethod.GET, description: 'Filter option lists for the investors grid' },
    TERMS: { path: '/funds/:id/terms', method: HttpMethod.GET, description: 'Fund terms (raw term/commitment/fee columns + resolved effective dates + fee periods)' },
    FEE_PERIODS_SAVE: { path: '/funds/:id/fee-periods', method: HttpMethod.POST, description: 'Full-replacement save of the fund fee-period schedule' },
    WATERFALL: { path: '/funds/:id/waterfall', method: HttpMethod.GET, description: 'The fund distribution waterfall: open waterfall header, tiers with their splits, GP carry participants, the booked-distribution lock' },
    WATERFALL_SAVE: { path: '/funds/:id/waterfall', method: HttpMethod.POST, description: 'Save the fund waterfall (header + replacement set of tiers / splits; 422 with the rule on refusal)' },
    WATERFALL_CREATE_FROM_TEMPLATE: { path: '/funds/:id/waterfall/create-from-template', method: HttpMethod.POST, description: 'Copy a waterfall template (cfg.waterfall_templates) onto a fund with no open waterfall' },
    WATERFALL_CREATE_YEAR_POOLS: { path: '/funds/:id/waterfall/year-pools', method: HttpMethod.POST, description: 'Create the year pools of the fund waterfall: a pool per calendar year with capital contributed or an investment bought, every investment assigned to the pool of its purchase year. Adds only; safe to repeat' },
    WATERFALL_MOVE_TRANCHE: { path: '/funds/:id/waterfall/pools/move-tranche', method: HttpMethod.POST, description: 'Move one investment (an ultimate-parent tranche) to another pool of the fund waterfall; 422 once a distribution is booked' },
    WATERFALL_POSITION: { path: '/funds/:id/waterfall/position', method: HttpMethod.GET, description: 'Waterfall position of a fund as at a date (?as_of=): each partner\'s booked waterfall state and the hypothetical liquidation of its capital account at fair market value - the accrued carried interest; data null without an open waterfall' },
    NOTICE_SETTINGS: { path: '/funds/:id/notice-settings', method: HttpMethod.GET, description: 'Capital call notice settings of a fund (layout, wording overrides, bank account) + the active layouts' },
    NOTICE_SETTINGS_PATCH: { path: '/funds/notice-settings', method: HttpMethod.PATCH, description: 'Patch one notice setting of a fund (InputManager auto-patch: { id: fund id, column: value })' },
    NOTICE_BANK_ACCOUNT_PATCH: { path: '/funds/notice-bank-account', method: HttpMethod.PATCH, description: 'Patch one column of the bank account a fund is paid capital calls into (InputManager auto-patch)' },
    STATEMENT_SETTINGS: { path: '/funds/:id/statement-settings', method: HttpMethod.GET, description: 'Investor statement settings of a fund (layout, partners capital page, wording overrides) + the layouts and the default wording' },
    STATEMENT_SETTINGS_PATCH: { path: '/funds/statement-settings', method: HttpMethod.PATCH, description: 'Patch one investor statement setting of a fund (InputManager auto-patch: { id: fund id, column: value })' },
    TRIAL_BALANCE: { path: '/funds/:id/trial-balance', method: HttpMethod.GET, description: 'Per-account opening/movement/closing balances (fund Ledger tab)' },
    GL_DETAIL: { path: '/funds/:id/gl-detail', method: HttpMethod.GET, description: 'Journal-line drill-down for one chart-of-accounts account' },
    FIN_YEARS: { path: '/funds/:id/fin-years', method: HttpMethod.GET, description: 'Financial-year options for the Ledger tab' },
    STATEMENT_OPTIONS: { path: '/funds/:id/statements/options', method: HttpMethod.GET, description: 'Statement-type + LP selector lists (fund Statements tab)' },
    STATEMENT: { path: '/funds/:id/statements/statement', method: HttpMethod.GET, description: 'Income Statement / Balance Sheet row stream from the cfg layout' },
    PARTNERS_CAPITAL: { path: '/funds/:id/statements/partners-capital', method: HttpMethod.GET, description: 'Statement of Partners Capital (per-LP roll-forward + fund total)' },
    LP_CAPITAL_ACCOUNT: { path: '/funds/:id/statements/lp-capital-account', method: HttpMethod.GET, description: 'Single-LP capital account statement' },
    STATEMENT_CHECKS: { path: '/funds/:id/statements/checks', method: HttpMethod.GET, description: 'Whether the investor statements of a fund can be made for a period end: reconciliation, blocking checks, warnings (?period_end=)' },
    FIN_YEAR_CLOSE: { path: '/funds/:id/fin-years/:fyId/close', method: HttpMethod.POST, description: 'Year-end close: on the Financial year basis book the partner allocation (body data.overrides), then the closing journals into Accumulated net income' },
    FIN_YEAR_REOPEN: { path: '/funds/:id/fin-years/:fyId/reopen', method: HttpMethod.POST, description: 'Reopen a closed financial year (removes its closing journals and the partner allocation lines the close booked)' },
    FIN_YEAR_ALLOCATION: { path: '/funds/:id/fin-years/:fyId/allocation', method: HttpMethod.GET, description: 'The Close year dialog\'s read: the curative partner allocation of the year per partner and K-1 tax category (Calculated / Override, the carry-forward, the unmapped accounts)' },
    PARTNER_ALLOCATION: { path: '/funds/:id/fin-years/:fyId/partner-allocation', method: HttpMethod.GET, description: 'The Partner allocation report: per partner and K-1 tax category the fund total, pro rata, curative, allocated (booked or unbooked), carry-forward, the partner\'s own lines; totals per category tied to the ledger (?as_of= inside an open year)' },
    PARTNER_ALLOCATION_WORKBOOK: { path: '/funds/:id/fin-years/:fyId/partner-allocation/workbook', method: HttpMethod.GET, description: 'The Partner allocation workbook (xlsx): the report and the audit detail sheet - every allocation line with the population and share used, the partner\'s own lines, the tie to the capital account statement (?as_of=)' },
    REVIEW_WORKBOOK: { path: '/funds/:id/review-workbook', method: HttpMethod.GET, description: 'The Review workbook (xlsx) of a fund as of a period end (?as_of=): the balance sheet, the income statement, the schedule of investments, the partners capital, the trial balance and the journal lines since inception as linked tabs - every statement figure a formula over the lines beside the engine figure' },
    CHART_OF_ACCOUNTS: { path: '/funds/:id/chart-of-accounts', method: HttpMethod.GET, description: 'Fund chart state: catalogue accounts (active + usage) and item->account mappings' },
    CHART_OF_ACCOUNTS_OPTIONS: { path: '/funds/:id/chart-of-accounts/options', method: HttpMethod.GET, description: 'Select lists for the Chart of Accounts tab (GL accounts, source funds, templates)' },
    CHART_CREATE_FULL: { path: '/funds/:id/chart-of-accounts/create-full', method: HttpMethod.POST, description: 'Instantiate the whole GL catalogue as the fund chart' },
    CHART_COPY_FROM_FUND: { path: '/funds/:id/chart-of-accounts/copy-from-fund', method: HttpMethod.POST, description: 'Copy another fund chart (accounts + item overrides) onto this fund' },
    CHART_CREATE_FROM_TEMPLATE: { path: '/funds/:id/chart-of-accounts/create-from-template', method: HttpMethod.POST, description: 'Instantiate a named chart template onto this fund' },
    CHART_ACCOUNTS_SAVE: { path: '/funds/:id/chart-of-accounts/accounts', method: HttpMethod.POST, description: 'Activate / deactivate the fund chart accounts (full grid state)' },
    CHART_REALLOCATE_ITEM: { path: '/funds/:id/chart-of-accounts/reallocate', method: HttpMethod.POST, description: 'Repoint an accounting item and migrate its journals (restate open years, reclassify closed)' },
    CHART_SAVE_AS_TEMPLATE: { path: '/funds/:id/chart-of-accounts/save-as-template', method: HttpMethod.POST, description: 'Snapshot this fund chart as a named chart template' },
    CREATE: { path: '/funds', method: HttpMethod.POST, description: 'Create a fund (New Fund form; app admins only)' },
    PATCH: { path: '/funds', method: HttpMethod.PATCH, description: 'Patch fund identity/term/commitment/fee fields' },
    COMMITMENT_OPTIONS: { path: '/funds/:id/commitment-options', method: HttpMethod.GET, description: 'Fund context of the Investors tab (currency, consolidated-fund membership) and its select lists' },
    COMMITMENT_FORM_OPTIONS: { path: '/funds/commitment-form-options', method: HttpMethod.GET, description: 'Select lists of the Commitment forms: an account\'s investors (?account_id=), standalone funds, consolidated funds with their members, statuses, additional notice documents' },
    COMMITMENT_GET: { path: '/funds/commitments/:id', method: HttpMethod.GET, description: 'One fund commitment for the Commitment form (a standalone commitment or a pocket of a consolidated one)' },
    COMMITMENT_SAVE: { path: '/funds/commitments', method: HttpMethod.POST, description: 'Create / edit a standalone fund commitment (optionally creating the investor entity)' },
    COMMITMENT_TRASH: { path: '/funds/commitments/:id', method: HttpMethod.DELETE, description: 'Soft-delete a standalone fund commitment' }
  },

  // ============================================================================
  // INVESTORS ROUTES — the Investor form (an investor entity, public.investors)
  // ============================================================================
  INVESTORS: {
    FORM_OPTIONS: { path: '/investors/form-options', method: HttpMethod.GET, description: 'Select lists of the Investor form that are not in the global configs (legal entity types, additional notice documents)' },
    GET: { path: '/investors/:id', method: HttpMethod.GET, description: 'One investor entity: its fields, its account and its default additional notice documents' },
    COMMITMENTS: { path: '/investors/:id/commitments', method: HttpMethod.GET, description: 'The investor\'s standalone and consolidated commitments, each with the notice documents it carries' },
    CREATE: { path: '/investors', method: HttpMethod.POST, description: 'Create an investor entity under an account' },
    PATCH: { path: '/investors', method: HttpMethod.PATCH, description: 'Edit an investor entity (only the changed fields; null clears)' }
  },

  // ============================================================================
  // FUND TRANSACTIONS ROUTES
  // ============================================================================
  FUND_TRANSACTIONS: {
    FILTER: { path: '/fund-transactions/filter', method: HttpMethod.POST, description: 'Filtered/sorted/paginated fund transactions grid' },
    FILTER_OPTIONS: { path: '/fund-transactions/filter-options', method: HttpMethod.GET, description: 'Filter option lists for the fund transactions grid' },
    GET: { path: '/fund-transactions/:id', method: HttpMethod.GET, description: 'Fund transaction detail (header + items + journals + totals)' },
    SAVE: { path: '/fund-transactions', method: HttpMethod.POST, description: 'Create/update a fund transaction (returns refreshed detail incl. journal preview)' },
    BOOK: { path: '/fund-transactions/:id/book', method: HttpMethod.POST, description: 'Book a balanced draft transaction' },
    UNBOOK: { path: '/fund-transactions/:id/unbook', method: HttpMethod.POST, description: 'Revert a booked transaction to draft' },
    DELETE: { path: '/fund-transactions/:id', method: HttpMethod.DELETE, description: 'Soft-trash a fund transaction (header + items + journals)' },
    CLOSING_CANDIDATES: { path: '/fund-transactions/fund/:fundId/closing-candidates', method: HttpMethod.GET, description: 'Live standalone commitments of a fund with their selection state for a Closing transaction' },
    EARLIER_YEARS: { path: '/fund-transactions/:id/earlier-years', method: HttpMethod.GET, description: 'The Closing form\'s Earlier years read: what the partners a closing admits take extra of per tax category (the engine\'s carry-forward at the closing date; a Draft previewed with its selection applied, nothing written)' },
    TRANSFER_CANDIDATES: { path: '/fund-transactions/fund/:fundId/transfer-candidates', method: HttpMethod.GET, description: 'Live standalone commitments of a fund with position, terms and selection state for a Transfer of Interest transaction' },
    TRANSFER_PREVIEW: { path: '/fund-transactions/fund/:fundId/transfer-preview', method: HttpMethod.GET, description: 'A commitment position as at a transfer date (called / paid / capital balance) - ?fund_commitment_id=&date=' },
    FEE_POSITION: { path: '/fund-transactions/fund/:fundId/fee-position', method: HttpMethod.GET, description: 'Fees due vs booked per commitment that holds an interest on the charge date (Fee Booking workbench) - ?split_date=&period_to=&charge_date=' },
    FEE_BASIS: { path: '/fund-transactions/fund/:fundId/fee-basis', method: HttpMethod.GET, description: 'Fund-level fee calculation basis for one fee window (Fee Booking workbench card)' },
    FEE_HISTORY: { path: '/fund-transactions/fund/:fundId/fee-history', method: HttpMethod.GET, description: 'Fee-period fund transactions with their fund-level basis, newest first (Fee Booking workbench)' }
  },

  // ============================================================================
  // INTEREST ACCRUALS ROUTES (workbench - docs/INTEREST_ACCRUAL_DESIGN.md 6.1)
  // ============================================================================
  INTEREST_ACCRUALS: {
    POSITION: { path: '/interest-accruals/fund/:fundId/position', method: HttpMethod.GET, description: 'Interest accrued to date vs booked per rated tranche (receivable) or fund loan (payable), with schedules, totals and journal preview - ?as_of=&side=' },
    RUNS: { path: '/interest-accruals/fund/:fundId/runs', method: HttpMethod.GET, description: "A fund's interest accrual runs, newest as-of first - ?side= optional" },
    CREATE_RUN: { path: '/interest-accruals/runs', method: HttpMethod.POST, description: 'Create and book one interest accrual run (atomic) - {data:{fund_id, as_of, side, lines, notes}}' },
    RUN: { path: '/interest-accruals/runs/:id', method: HttpMethod.GET, description: 'One interest accrual run with its lines' },
    BOOK_RUN: { path: '/interest-accruals/runs/:id/book', method: HttpMethod.POST, description: "Book a Draft run's Draft transactions" },
    UNBOOK_RUN: { path: '/interest-accruals/runs/:id/unbook', method: HttpMethod.POST, description: 'Unbook a Booked run (transactions back to Draft) - {data:{dry_run}}' },
    DELETE_RUN: { path: '/interest-accruals/runs/:id', method: HttpMethod.DELETE, description: 'Trash a Draft run with its Draft transactions' }
  },

  // ============================================================================
  // ASSET TRANSACTIONS ROUTES
  // ============================================================================
  ASSET_TRANSACTIONS: {
    FILTER: { path: '/asset-transactions/filter', method: HttpMethod.POST, description: 'Filtered/sorted/paginated asset transactions grid' },
    FILTER_OPTIONS: { path: '/asset-transactions/filter-options', method: HttpMethod.GET, description: 'Filter option lists for the asset transactions grid' },
    OPTIONS: { path: '/asset-transactions/options', method: HttpMethod.GET, description: 'Booking-form header options (companies + deals)' },
    COMPANY_OPTIONS: { path: '/asset-transactions/company-options', method: HttpMethod.GET, description: 'Per-company securities + open tranches with held measures' },
    VALUATION_CURRENCIES: { path: '/asset-transactions/valuation-currencies', method: HttpMethod.GET, description: 'Functional currencies of funds holding in-scope positions for a valuation' },
    GET: { path: '/asset-transactions/:id', method: HttpMethod.GET, description: 'Asset transaction detail (header + journals + totals + COA flag)' },
    SAVE: { path: '/asset-transactions', method: HttpMethod.POST, description: 'Create/update a Draft asset transaction (returns refreshed detail incl. journal preview)' },
    CREATE_SECURITY: { path: '/asset-transactions/securities', method: HttpMethod.POST, description: 'Create a security from the booking form (+ New security)' },
    BOOK: { path: '/asset-transactions/:id/book', method: HttpMethod.POST, description: 'Book a draft through the asset engine (COA + balance guarded)' },
    UNBOOK: { path: '/asset-transactions/:id/unbook', method: HttpMethod.POST, description: 'Reverse a booking (engine sweep incl. progeny) but keep the transaction as a Draft' },
    RESET_JOURNALS: { path: '/asset-transactions/:id/reset-journals', method: HttpMethod.POST, description: 'Discard user journal edits and regenerate engine defaults' },
    DELETE: { path: '/asset-transactions/:id', method: HttpMethod.DELETE, description: 'Trash an asset transaction (Draft: header+preview; Booked: engine sweep)' },
    TRANCHE_DETAIL: { path: '/tranches/:id', method: HttpMethod.GET, description: 'Tranche full-form read (facts + current position + chronological history)' },
    SECURITY_DETAIL: { path: '/securities/:id', method: HttpMethod.GET, description: 'Security full-form read (identity + key terms + per-tranche holdings)' },
    SECURITY_SAVE: { path: '/securities/:id', method: HttpMethod.POST, description: 'Save the editable fields of a security (Security full-form)' },
    SECURITY_DELETE: { path: '/securities/:id', method: HttpMethod.DELETE, description: 'Soft-delete a security (guarded: refused while tranches/transactions/valuations reference it)' },
    TRANCHES_FILTER: { path: '/tranches/filter', method: HttpMethod.POST, description: 'Filtered/sorted/paginated tranches grid (Operations > Tranches)' },
    TRANCHES_FILTER_OPTIONS: { path: '/tranches/filter-options', method: HttpMethod.GET, description: 'Filter option lists for the tranches grid' }
  },

  // ============================================================================
  // CAPITAL CALLS ROUTES
  // ============================================================================
  CAPITAL_CALLS: {
    FILTER: { path: '/capital-calls/filter', method: HttpMethod.POST, description: 'Filtered/sorted/paginated capital calls grid' },
    FILTER_OPTIONS: { path: '/capital-calls/filter-options', method: HttpMethod.GET, description: 'Filter option lists for the capital calls grid' },
    GET: { path: '/capital-calls/:id', method: HttpMethod.GET, description: 'Capital call detail (header + items + per-commitment rows + linked transaction)' },
    SAVE: { path: '/capital-calls', method: HttpMethod.POST, description: 'Create/update a Draft capital call (returns refreshed detail)' },
    COMPUTE: { path: '/capital-calls/:id/compute', method: HttpMethod.POST, description: 'Recompute per-commitment amounts (fee engine / pro-rata allocator)' },
    CLEAR_OVERRIDES: { path: '/capital-calls/:id/clear-overrides', method: HttpMethod.POST, description: 'Clear all per-commitment overrides on a Draft call (Recompute runs this first)' },
    ISSUE: { path: '/capital-calls/:id/issue', method: HttpMethod.POST, description: 'Issue a Draft call (books the Capital Call fund transaction atomically)' },
    REVERT: { path: '/capital-calls/:id/revert', method: HttpMethod.POST, description: 'Revert an Issued call to Draft (unbooks + trashes its transaction)' },
    RENAME: { path: '/capital-calls/:id/rename', method: HttpMethod.POST, description: 'Rename a call in any status (an Issued call needs no revert); booked descriptions copied from the old name follow' },
    DELETE: { path: '/capital-calls/:id', method: HttpMethod.DELETE, description: 'Soft-trash a Draft capital call' },
    NOTICE_DATA: { path: '/capital-calls/:id/notice-data', method: HttpMethod.GET, description: 'What the notices of a call are built from: per-LP lines and position, notice period given vs required, problems' },
    NOTICES: { path: '/capital-calls/:id/notices', method: HttpMethod.GET, description: 'The stored notices of a call: per LP the notice shown (status, file, out of date, stalled), the problems that stop them being produced, counts' },
    NOTICES_GENERATE: { path: '/capital-calls/:id/notices/generate', method: HttpMethod.POST, description: 'Generate (or regenerate) the stored notices of an issued call, all LPs or data.fund_commitment_ids; answers once they are queued (422 = nothing could be queued, with the reason)' },
    NOTICE_PURPOSE_PATCH: { path: '/capital-calls/notice-purpose', method: HttpMethod.PATCH, description: 'Patch the notice purpose of a call in any status (InputManager auto-patch: { id: capital call id, notice_purpose })' },
    NOTICE_CAMPAIGN_PLAN: { path: '/capital-calls/:id/notice-campaign/plan', method: HttpMethod.GET, description: 'What "Send notices" would create: per LP account its notices, recipients and merge fields, what blocks the send (?only_unsent=true leaves out notices already sent)' },
    NOTICE_CAMPAIGN_CREATE: { path: '/capital-calls/:id/notice-campaign', method: HttpMethod.POST, description: 'Create the draft notice email of an issued call (data.only_unsent); 422 = the notices cannot be sent, with the reason' },
    NOTICE_CAMPAIGN_REFRESH: { path: '/capital-calls/:id/notice-campaign/:campaignId/refresh', method: HttpMethod.POST, description: 'Rebuild a draft notice email from the call as it is now: recipients and per-account notices' },
    PAYMENT_STATUS: { path: '/capital-calls/fund/:fundId/payment-status', method: HttpMethod.GET, description: 'FIFO paid-status rows for a fund (per commitment x call + excess)' },
    CONTRIBUTIONS: { path: '/capital-calls/fund/:fundId/contributions', method: HttpMethod.GET, description: 'Live contributions of a fund with receipt-transaction status' },
    COMMITMENT_OPTIONS: { path: '/capital-calls/fund/:fundId/commitment-options', method: HttpMethod.GET, description: 'Closed-commitment options for the record-payment select' },
    RECORD_CONTRIBUTION: { path: '/capital-calls/contributions', method: HttpMethod.POST, description: 'Record an LP payment (books the Capital Call Receipt; 409 = duplicate warning)' },
    RECORD_CONTRIBUTIONS_BATCH: { path: '/capital-calls/contributions/batch', method: HttpMethod.POST, description: 'Record N LP payments in one all-or-nothing batch (409 = duplicate warning, 422 = row validation errors)' },
    CONTRIBUTION_DETAIL: { path: '/capital-calls/contributions/:id/detail', method: HttpMethod.GET, description: 'One contribution, in the fund-list row shape, for the payment editor' },
    UPDATE_CONTRIBUTION: { path: '/capital-calls/contributions/:id', method: HttpMethod.PUT, description: 'Edit a booked payment in place (unbooks the receipt, rewrites it, rebooks; 409 = duplicate warning)' },
    DELETE_CONTRIBUTION: { path: '/capital-calls/contributions/:id', method: HttpMethod.DELETE, description: 'Delete a contribution (trashes its receipt transaction)' },
    POSITION: { path: '/capital-calls/fund/:fundId/position', method: HttpMethod.GET, description: 'Called/uncalled/paid/outstanding per commitment + fund summary (Capital Call Position)' },
    EQUALIZATION_PREVIEW: { path: '/capital-calls/fund/:fundId/equalization-preview', method: HttpMethod.GET, description: 'Outstanding late-closer catch-up per commitment (target vs issued delta)' },
    EQUALIZATION_CALL: { path: '/capital-calls/fund/:fundId/equalization-call', method: HttpMethod.POST, description: 'Draft an equalization call pre-populated with catch-up items (422 = policy/nothing outstanding)' }
  },

  // ============================================================================
  // DISTRIBUTIONS ROUTES (docs/WATERFALL_CARRY_DESIGN.md, phase 3)
  // ============================================================================
  DISTRIBUTIONS: {
    GET: { path: '/distributions/:id', method: HttpMethod.GET, description: 'Distribution detail (header + sources + per-commitment lines + linked transaction + totals)' },
    SAVE: { path: '/distributions', method: HttpMethod.POST, description: 'Create/update a Draft distribution (header, replacement set of sources, the overrides per partner and class and per priority line, the SBA override reason; returns refreshed detail)' },
    COMPUTE: { path: '/distributions/:id/compute', method: HttpMethod.POST, description: 'Compute the lines: the priority lines, the SBA gates and the fund waterfall on the gross amount' },
    ISSUE: { path: '/distributions/:id/issue', method: HttpMethod.POST, description: 'Issue a Draft distribution (books the Distribution fund transaction per commitment atomically)' },
    REVERT: { path: '/distributions/:id/revert', method: HttpMethod.POST, description: 'Revert an Issued distribution to Draft (unbooks + trashes its transaction; refused while a later one is Issued)' },
    DELETE: { path: '/distributions/:id', method: HttpMethod.DELETE, description: 'Soft-trash a Draft distribution' },
    WORKBOOK: { path: '/distributions/:id/workbook', method: HttpMethod.GET, description: 'The audit workbook (xlsx): the allocation with the tier arithmetic as formulas; 400 before Compute' },
    FUND_LIST: { path: '/distributions/fund/:fundId', method: HttpMethod.GET, description: 'The distributions of a fund (the fund Distributions tab)' },
    SOURCE_OPTIONS: { path: '/distributions/fund/:fundId/source-options', method: HttpMethod.GET, description: 'The Booked realizations of a fund a distribution can name as its sources' }
  },

  // ============================================================================
  // CONSOLIDATED FUNDS (FUND GROUPS) ROUTES
  // ============================================================================
  FUND_GROUPS: {
    FILTER: { path: '/fund-groups/filter', method: HttpMethod.POST, description: 'Consolidated Funds grid (one row per live fund group)' },
    DASHBOARD: { path: '/fund-groups/:id', method: HttpMethod.GET, description: 'Three-level committed/called KPI block (consolidated / per member pocket)' },
    DETAIL: { path: '/fund-groups/:id/detail', method: HttpMethod.GET, description: 'Raw group form read (header + member set)' },
    SAVE: { path: '/fund-groups', method: HttpMethod.POST, description: 'Create/update a fund group (header + full member set)' },
    DELETE: { path: '/fund-groups/:id', method: HttpMethod.DELETE, description: 'Trash a fund group (422 while live subscriptions exist)' },
    SUBSCRIPTIONS_FILTER: { path: '/fund-groups/:id/subscriptions/filter', method: HttpMethod.POST, description: 'Paginated / filtered / sorted subscription grid rows of a group' },
    SUBSCRIPTIONS_FILTER_OPTIONS: { path: '/fund-groups/:id/subscriptions/filter-options', method: HttpMethod.GET, description: 'Filter option lists for the subscriptions grid' },
    GET_SUBSCRIPTION: { path: '/fund-groups/subscriptions/:id', method: HttpMethod.GET, description: 'One consolidated commitment for its form: its fields, one allocation entry per member fund, the notice setting' },
    SAVE_SUBSCRIPTION: { path: '/fund-groups/subscriptions', method: HttpMethod.POST, description: 'Create/update a subscription + allocations (409 = pocket below called-to-date warning)' },
    DELETE_SUBSCRIPTION: { path: '/fund-groups/subscriptions/:id', method: HttpMethod.DELETE, description: 'Trash a subscription (blocked while pockets carry call/contribution records)' },
    CALL_POSITION: { path: '/fund-groups/:id/call-position', method: HttpMethod.GET, description: 'Consolidated call position by account across member funds' },
    CALLS: { path: '/fund-groups/:id/calls', method: HttpMethod.GET, description: 'Consolidated calls list (Calls tab)' },
    CALL_DETAIL: { path: '/fund-groups/calls/:id', method: HttpMethod.GET, description: 'Consolidated call detail (header + per-subscription split + linked calls + transfer legs)' },
    CALL_SAVE: { path: '/fund-groups/calls', method: HttpMethod.POST, description: 'Create/update a Draft consolidated call plan' },
    CALL_ISSUE: { path: '/fund-groups/calls/:id/issue', method: HttpMethod.POST, description: 'Refresh the subscription-share split + issue the gross call on the primary fund' },
    CALL_REVERT: { path: '/fund-groups/calls/:id/revert', method: HttpMethod.POST, description: 'Revert an Issued consolidated call to an editable Draft (422 with guidance when blocked)' },
    CALL_REFRESH_SPLIT: { path: '/fund-groups/calls/:id/refresh-split', method: HttpMethod.POST, description: 'Compute the linked call and pin non-fee lines to subscription shares, keeping per-investor amounts a person edited (Draft preview)' },
    CALL_DELETE: { path: '/fund-groups/calls/:id', method: HttpMethod.DELETE, description: 'Trash a Draft consolidated call plan (and its linked draft call)' },
    TRANSFERS: { path: '/fund-groups/:id/transfers', method: HttpMethod.GET, description: 'Intercompany transfers grid (Draft + Booked; investment / expenses / fee-funding buckets)' },
    TRANSFER_POOL: { path: '/fund-groups/:id/transfer-pool', method: HttpMethod.GET, description: 'Transfer pool: called / transferred / retained per bucket across every issued consolidated call' },
    TRANSFER_DETAIL: { path: '/fund-groups/transfers/:id', method: HttpMethod.GET, description: 'Transfer detail (buckets + per-LP lines + derived satellite call)' },
    TRANSFER_DEFAULTS: { path: '/fund-groups/:id/transfer-defaults', method: HttpMethod.GET, description: 'Satellites, equalization source options and draft seeds for the New Transfer modal (?fund_id=&fund_group_call_id=)' },
    TRANSFER_CREATE: { path: '/fund-groups/transfers', method: HttpMethod.POST, description: 'Create a Draft transfer + its derived satellite call (edited in the Capital Call form)' },
    TRANSFER_BOOK: { path: '/fund-groups/transfers/:id/book', method: HttpMethod.POST, description: 'Book a Draft transfer (409 = over-called confirm-through)' },
    TRANSFER_REVERT: { path: '/fund-groups/transfers/:id/revert', method: HttpMethod.POST, description: 'Revert a booked transfer to Draft (unbook + trash the pair and contributions; the derived call returns to Draft)' },
    TRANSFER_DELETE: { path: '/fund-groups/transfers/:id', method: HttpMethod.DELETE, description: 'Delete a Draft transfer and its derived call' },
    FEE_REALLOCATION: { path: '/fund-groups/:id/fee-reallocation', method: HttpMethod.GET, description: 'Late-joiner fee reallocation per satellite on the Reallocate-fees-to-date policy (?as_of=): fee true-up positions, contribution-bucket reclass rows, events' },
    FEE_REALLOCATION_ADVANCE: { path: '/fund-groups/fee-reallocations', method: HttpMethod.POST, description: 'Draft the due reallocation steps for (group, satellite, as_of): fee true-ups and the contribution reclass journal entries' },
    FEE_REALLOCATION_DELETE: { path: '/fund-groups/fee-reallocations/:id', method: HttpMethod.DELETE, description: 'Delete a reallocation event whose records are all still Draft' },
    EQUALIZATION_PREVIEW: { path: '/fund-groups/:id/equalization-preview', method: HttpMethod.GET, description: 'Group-aware late-closer equalization preview (subscription-level targets; preview only)' },
    TRIAL_BALANCE: { path: '/fund-groups/:id/trial-balance', method: HttpMethod.GET, description: 'Consolidated workpaper trial balance (per-member closings + eliminations + consolidated)' },
    GL_DETAIL: { path: '/fund-groups/:id/gl-detail', method: HttpMethod.GET, description: 'Consolidated journal drill-down for one GL account across member funds (?gl_account_id=)' },
    STATEMENT_OPTIONS: { path: '/fund-groups/:id/statements/options', method: HttpMethod.GET, description: 'Statement-type + subscription selector lists for the consolidated Statements tab' },
    STATEMENT: { path: '/fund-groups/:id/statements/statement', method: HttpMethod.GET, description: 'Consolidated Income Statement / Balance Sheet from the shared cfg layout (?code=)' },
    PARTNERS_CAPITAL: { path: '/fund-groups/:id/statements/partners-capital', method: HttpMethod.GET, description: 'Consolidated partners-capital roll-forward (one row per subscription)' },
    LP_CAPITAL_ACCOUNT: { path: '/fund-groups/:id/statements/lp-capital-account', method: HttpMethod.GET, description: 'Single-subscription capital account statement (?fund_group_commitment_id=)' },
    STATEMENT_CHECKS: { path: '/fund-groups/:id/statements/checks', method: HttpMethod.GET, description: 'Whether the investor statements of a consolidated fund can be made for a period end, per legal entity (?period_end=)' },
    STATEMENT_SETTINGS: { path: '/fund-groups/:id/statement-settings', method: HttpMethod.GET, description: 'Investor statement settings of a consolidated fund (layout, partners capital page, entity statements, wording overrides)' },
    STATEMENT_SETTINGS_PATCH: { path: '/fund-groups/statement-settings', method: HttpMethod.PATCH, description: 'Patch one investor statement setting of a consolidated fund (InputManager auto-patch: { id: consolidated fund id, column: value })' },
    // consolidated distributions (docs/WATERFALL_CARRY_DESIGN.md, phase 5):
    // a header + one distributions row per legal entity; the writers return
    // the LEAD distribution's detail (the shape of GET /distributions/:id)
    DISTRIBUTIONS: { path: '/fund-groups/:id/distributions', method: HttpMethod.GET, description: 'Consolidated distributions list (the Distributions tab): one row per header with its entities' },
    DISTRIBUTION_SOURCE_OPTIONS: { path: '/fund-groups/:id/distribution-source-options', method: HttpMethod.GET, description: 'The Booked realizations of every member fund a consolidated distribution can name as its sources' },
    DISTRIBUTION_SAVE: { path: '/fund-groups/distributions', method: HttpMethod.POST, description: 'Create (fund_group_id) or update (fund_group_distribution_id) a Draft consolidated distribution: the lead inputs, no overrides; returns the lead detail' },
    DISTRIBUTION_COMPUTE: { path: '/fund-groups/distributions/:id/compute', method: HttpMethod.POST, description: 'Compute every entity of a consolidated distribution (its part of the proceeds, its waterfall); returns the lead detail' },
    DISTRIBUTION_ISSUE: { path: '/fund-groups/distributions/:id/issue', method: HttpMethod.POST, description: 'Issue a Draft consolidated distribution: every entity books its share atomically; returns the lead detail' },
    DISTRIBUTION_REVERT: { path: '/fund-groups/distributions/:id/revert', method: HttpMethod.POST, description: 'Revert an Issued consolidated distribution to Draft on every entity; returns the lead detail' },
    DISTRIBUTION_DELETE: { path: '/fund-groups/distributions/:id', method: HttpMethod.DELETE, description: 'Soft-trash a Draft consolidated distribution and its entity rows' },
    WATERFALL_POSITION: { path: '/fund-groups/:id/waterfall/position', method: HttpMethod.GET, description: 'Waterfall position of a consolidated fund as at a date (?as_of=): the fund position model with the consolidated fund as its fund_id; data null without an open waterfall' }
  },

  // ============================================================================
  // JOURNAL ENTRIES ROUTES (standalone journal entries, spec A3.5)
  // ============================================================================
  JOURNAL_ENTRIES: {
    FILTER: { path: '/journal-entries/filter', method: HttpMethod.POST, description: 'Paginated / filtered / sorted journal-entry grid rows' },
    FILTER_OPTIONS: { path: '/journal-entries/filter-options', method: HttpMethod.GET, description: 'Filter option lists for the journal-entries grid' },
    OPTIONS: { path: '/journal-entries/options', method: HttpMethod.GET, description: 'Line/link option lists for the entry form (LPs, fund + asset transactions of a fund)' },
    DETAIL: { path: '/journal-entries/:id', method: HttpMethod.GET, description: 'Full journal-entry detail (entry + lines + totals)' },
    SAVE: { path: '/journal-entries', method: HttpMethod.POST, description: 'Create/update a journal entry (header + full line set, Draft only)' },
    BOOK: { path: '/journal-entries/:id/book', method: HttpMethod.POST, description: 'Book a balanced journal entry' },
    UNBOOK: { path: '/journal-entries/:id/unbook', method: HttpMethod.POST, description: 'Revert a booked journal entry to Draft' },
    TRASH: { path: '/journal-entries/:id', method: HttpMethod.DELETE, description: 'Soft-delete a journal entry and its lines' }
  },
  // Investor statement runs (spec A3.14 stage 5): a quarter's statements for
  // one fund or many - generated, stored and reviewed. Stored PDFs download
  // through FILES.SIGNED_URL.
  INVESTOR_STATEMENTS: {
    FILTER: { path: '/investor-statements/filter', method: HttpMethod.POST, description: 'Paginated / filtered / sorted statement runs grid rows' },
    FILTER_OPTIONS: { path: '/investor-statements/filter-options', method: HttpMethod.GET, description: 'Filter option lists for the statement runs grid' },
    RUN: { path: '/investor-statements/runs/:id', method: HttpMethod.GET, description: 'One run: its details, its funds with where each stands, and the statements stored for them' },
    RUN_SAVE: { path: '/investor-statements/runs', method: HttpMethod.POST, description: 'Create a statement run (data: name, period_end, statement_date, notes); 422 = it cannot be saved that way, with the reason' },
    RUN_PATCH: { path: '/investor-statements/runs', method: HttpMethod.PATCH, description: 'Patch one field of a run (InputManager auto-patch: { id: run id, name | period_end | statement_date | notes })' },
    RUN_FUND_OPTIONS: { path: '/investor-statements/runs/:id/fund-options', method: HttpMethod.GET, description: 'The funds and consolidated funds that can join a run, with why one cannot and what its statements still need' },
    RUN_ADD_FUNDS: { path: '/investor-statements/runs/:id/funds', method: HttpMethod.POST, description: 'Add funds and consolidated funds to a run (data: fund_ids, fund_group_ids), all or nothing; 422 = refused, with the reason' },
    RUN_REMOVE_FUND: { path: '/investor-statements/run-funds/:id', method: HttpMethod.DELETE, description: 'Remove a fund from its run; its stored statements go to the trash' },
    RUN_GENERATE: { path: '/investor-statements/runs/:id/generate', method: HttpMethod.POST, description: 'Generate (or generate again) the documents of a run: every fund, data.run_fund_ids, or data.statement_ids of those funds; data.document_group_codes limits it to those document groups (reports, statements); a whole-run generation with the reports group also makes the investor performance deck of every account, and data.account_document_ids makes only those decks again; answers once the funds are queued (422 = nothing could be queued, with the reason)' },
    ACCOUNT_DOCUMENT_DELETE: { path: '/investor-statements/account-documents/:id', method: HttpMethod.DELETE, description: 'Delete one investor performance deck of a run (soft); its stored file goes to the trash. 422 = refused, with the reason (being generated; in a reports email on its way; issued and not an application administrator)' },
    RUN_DELETE: { path: '/investor-statements/runs/:id', method: HttpMethod.DELETE, description: 'Delete a run (soft); its stored statements go to the trash. 422 = refused, with the reason (being generated; issued statements and not an application administrator)' },
    RUN_FUND_ISSUE: { path: '/investor-statements/run-funds/:id/issue', method: HttpMethod.POST, description: 'Mark the documents of a fund of a run as issued - every document group, or data.document_group_codes (reports, statements); answers with the run (422 = refused, with the reason)' },
    RUN_FUND_UNISSUE: { path: '/investor-statements/run-funds/:id/unissue', method: HttpMethod.POST, description: 'Undo Mark as issued (an application administrator) - every document group, or data.document_group_codes; answers with the run' },
    RUN_REVIEW_WORKBOOKS: { path: '/investor-statements/runs/:id/review-workbooks', method: HttpMethod.GET, description: 'The Review workbooks of a run as one zip - one xlsx per legal fund as of the run period end, built live (?run_fund_id= for one fund of the run; a consolidated fund gives one per legal entity)' },
    RUN_FUND_CHANGES: { path: '/investor-statements/run-funds/:id/changes', method: HttpMethod.GET, description: 'Out of date: which stored statements of a fund of a run differ from the books and settings as they are now' },
    RUN_DESCRIPTION_WARNINGS: { path: '/investor-statements/runs/:id/description-warnings', method: HttpMethod.GET, description: "The agent-written company short descriptions the run's stored fund reports and performance decks printed that still need a person, per document: unreviewed (not signed off) or changed (edited since)" },
    FUND_RUNS: { path: '/investor-statements/funds/:id/runs', method: HttpMethod.GET, description: 'The statement runs a fund is in (its Statements tab)' },
    FUND_GROUP_RUNS: { path: '/investor-statements/fund-groups/:id/runs', method: HttpMethod.GET, description: 'The statement runs a consolidated fund is in (its Statements tab)' },
    SEND_PLAN: { path: '/investor-statements/runs/:id/send-plan', method: HttpMethod.GET, description: 'What "Send documents" would create: every fund of the run with whether it can be sent, per account its attachments (merged per fund where the fund merges on send), size against the mailbox cap, recipients and merge fields (?run_fund_ids=a,b limits the funds; ?only_unsent=true leaves out documents already sent; ?document_group_codes=reports limits the document groups)' },
    CAMPAIGN_CREATE: { path: '/investor-statements/runs/:id/campaign', method: HttpMethod.POST, description: 'Create the draft statement email of a run for the chosen funds and document groups (data.run_fund_ids, data.only_unsent, data.document_group_codes); 422 = the documents cannot be sent, with the reason' },
    CAMPAIGN_REFRESH: { path: '/investor-statements/runs/:id/campaign/:campaignId/refresh', method: HttpMethod.POST, description: 'Rebuild a draft statement email from the run as it is now: recipients and per-account attachments, for the same funds' }
  },

  // ============================================================================
  // COUNTRIES ROUTES
  // ============================================================================
  COUNTRIES: {
    LIST: { path: '/countries', method: HttpMethod.GET, description: 'List all countries' }
  },

  // ============================================================================
  // CITIES ROUTES
  // ============================================================================
  CITIES: {
    SEARCH: { path: '/cities/search', method: HttpMethod.GET, description: 'Search cities' }
  },

  // ============================================================================
  // CURRENCIES ROUTES
  // ============================================================================
  CURRENCIES: {
    LIST: { path: '/currencies', method: HttpMethod.GET, description: 'List all currencies' }
  },

  // ============================================================================
  // LOOKUPS ROUTES
  // ============================================================================
  LOOKUPS: {
    HEADERS: { path: '/lookups/headers', method: HttpMethod.GET, description: 'Get lookup headers' }
  },

  // ============================================================================
  // GLOBAL CONFIGS ROUTES
  // ============================================================================
  GLOBAL_CONFIGS: {
    LIST: { path: '/global-configs', method: HttpMethod.GET, description: 'Get all global configs' },
    REFRESH: {
      path: '/global-configs/refresh',
      method: HttpMethod.POST,
      description: 'Reload the backend global-configs cache after a cfg write (then re-GET to update Redux)'
    },
    VERSION: {
      path: '/global-configs/version',
      method: HttpMethod.GET,
      description: 'Cheap poll: {version, changed_at, poll_seconds}. Re-GET /global-configs only when version moves'
    },
    USER_PREFERENCES: {
      path: '/global-configs/user-preferences',
      method: HttpMethod.PATCH,
      description: 'Update user preferences (e.g. selected fund for Fundraising Pipeline)'
    },
    USER_PREFERENCES_DELETE: {
      path: '/global-configs/user-preferences',
      method: HttpMethod.DELETE,
      description: 'Delete user preference row for a menu item'
    },
    USER_PREFERENCES_APP: {
      path: '/global-configs/user-preferences/app',
      method: HttpMethod.PATCH,
      description: 'Upsert the per-user global (non-report) preference blob, e.g. the left-nav current area'
    }
  },

  // ============================================================================
  // CONTACT RELATIONSHIPS ROUTES
  // ============================================================================
  CONTACT_RELATIONSHIPS: {
    PROCESS_TEAM: {
      path: '/contact-relationships/process-company-team-relationships',
      method: HttpMethod.POST,
      description: 'Process company team relationships'
    },
    SET_PRIMARY_CONTACT: {
      path: '/contact-relationships/set-primary',
      method: HttpMethod.POST,
      description: 'Set primary contact relationship'
    }
  },

  // ============================================================================
  // REPORTING ROUTES
  // ============================================================================
  REPORTING: {
    MEASURES: {
      path: '/reporting/measures',
      method: HttpMethod.POST,
      description: 'Get investment measures'
    },
    SCHEDULE_OF_INVESTMENTS: {
      path: '/reporting/schedule-of-investments',
      method: HttpMethod.POST,
      description: 'Get Schedule of Investments data'
    },
    CASH_FLOWS: {
      path: '/reporting/cash-flows',
      method: HttpMethod.POST,
      description: 'Get Cash Flows data'
    }
  },

  // ============================================================================
  // DOCUMENT REPORT ROUTES (paginated PDF reports)
  // ============================================================================
  // Period Highlights — versioned fund / company highlight sets
  // (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md Phase 1). Backend module:
  // app_modules/period_highlights.
  PERIOD_HIGHLIGHTS: {
    LIST: {
      path: '/period-highlights',
      method: HttpMethod.POST,
      description: 'Period highlight sets (paginated, filtered, sorted)'
    },
    FILTER_OPTIONS: {
      path: '/period-highlights/filter-options',
      method: HttpMethod.POST,
      description: 'Dropdown options for the Period Highlights list filters'
    },
    GENERATE: {
      path: '/period-highlights/generate',
      method: HttpMethod.POST,
      description: 'Enqueue a period_highlights run for a fund, an LP (investor parent) or a company, and a period'
    },
    PROVISION_FUNDS: {
      path: '/period-highlights/provision-funds',
      method: HttpMethod.POST,
      description: 'Admin: one fund_performance_tracker instance per live fund'
    },
    PROVISION_LPS: {
      path: '/period-highlights/provision-lps',
      method: HttpMethod.POST,
      description: 'Admin: one lp_universe_tracker instance per investor parent with a closed commitment'
    },
    PROVISION_FUND_GROUPS: {
      path: '/period-highlights/provision-fund-groups',
      method: HttpMethod.POST,
      description: 'Admin: one fund_group_tracker instance per live consolidated fund'
    },
    GENERATE_HISTORY: {
      path: '/period-highlights/generate-history',
      method: HttpMethod.POST,
      description: 'Admin: enqueue period_highlights runs for every provisioned fund / consolidated fund / LP over the last N completed quarters (dry-run manifest first)'
    },
    STALENESS: {
      path: '/period-highlights/:setId/staleness',
      method: HttpMethod.GET,
      description: 'Recompute a set\'s frozen facts and report the values that moved since it was generated'
    },
    COMPANY: {
      path: '/period-highlights/company/:accountId',
      method: HttpMethod.GET,
      description: 'A company\'s highlight atoms across periods'
    },
    ITEM: {
      path: '/period-highlights/items/:itemId',
      method: HttpMethod.PATCH,
      description: 'Edit a highlight item on a draft or approved set, in place (headline, body, short text, include/exclude); figures are {tokens}'
    },
    NARRATIVE: {
      path: '/period-highlights/:setId/narrative',
      method: HttpMethod.PATCH,
      description: 'Edit a set\'s narrative on a draft or approved set, in place; figures are {tokens}'
    },
    SIGN_OFF: {
      path: '/period-highlights/:setId/sign-off',
      method: HttpMethod.POST,
      description: 'A human signs off an approved (auto-approved or edited-since) set at its current revision'
    },
    WITHDRAW_SIGN_OFF: {
      path: '/period-highlights/:setId/withdraw-sign-off',
      method: HttpMethod.POST,
      description: 'Take a sign-off back: the approved set reads "not reviewed" again in the reports'
    },
    REFRESH_METRICS: {
      path: '/period-highlights/:setId/refresh-metrics',
      method: HttpMethod.POST,
      description: 'Recompute a set\'s figures from the books and write them back (the prose is untouched)'
    },
    DETAIL: {
      path: '/period-highlights/:setId',
      method: HttpMethod.GET,
      description: 'One highlight set with items, resolved evidence and sibling versions'
    },
    APPROVE: {
      path: '/period-highlights/:setId/approve',
      method: HttpMethod.POST,
      description: 'Approve a draft set (becomes current; the previous current is superseded)'
    },
    REJECT: {
      path: '/period-highlights/:setId/reject',
      method: HttpMethod.POST,
      description: 'Reject a draft set with a reason'
    },
    CLONE_DRAFT: {
      path: '/period-highlights/:setId/clone-draft',
      method: HttpMethod.POST,
      description: 'Restore a superseded version: a new draft version carrying its wording, items, hand edits and stored figures'
    },
    REORDER: {
      path: '/period-highlights/:setId/reorder',
      method: HttpMethod.POST,
      description: 'Reorder the items of a draft set'
    },
    REFORMULATE: {
      path: '/period-highlights/:setId/reformulate',
      method: HttpMethod.POST,
      description: 'Regenerate the set as a new draft version'
    },
    NARRATIVE_REGENERATE: {
      path: '/period-highlights/:setId/narrative/regenerate',
      method: HttpMethod.POST,
      description: 'Regenerate the narrative over the current selection: a queued, narrative-only run written in place'
    },
    BENCH: {
      path: '/period-highlights/:setId/bench',
      method: HttpMethod.POST,
      description: 'Add the runners-up to this set: a queued, model-free pass appending excluded candidates the reviewer can swap in'
    },
    BENCH_BACKFILL: {
      path: '/period-highlights/bench-backfill',
      method: HttpMethod.POST,
      description: 'Admin: the runners-up backfill — one model-free pass per instance and completed quarter with a current approved set, dry-run manifest first'
    }
  },
  // Relationships — account owners, LP allocation, relationship facts, LP fund
  // exposure and the two cfg lookups (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md
  // Phase 0). Backend module: app_modules/relationships.
  LPS: {
    LIST: {
      path: '/lps',
      method: HttpMethod.POST,
      description: 'LPs grid: every LP (the LP Allocation row set) with team name, team-name-set, status, funds, commitments and owners'
    },
    FILTER_OPTIONS: {
      path: '/lps/filter-options',
      method: HttpMethod.POST,
      description: 'Dropdown options for the LPs grid filters'
    }
  },
  RELATIONSHIPS: {
    ACCOUNT_OWNERS: {
      path: '/accounts/:accountId/owners',
      method: HttpMethod.GET,
      description: 'Current and past owners (team members by role) of an account'
    },
    ACCOUNT_OWNER_SAVE: {
      path: '/account-owners',
      method: HttpMethod.POST,
      description: 'Assign a team member to an account in a role (an exclusive role closes the previous holder)'
    },
    ACCOUNT_OWNER_END: {
      path: '/account-owners/:ownerId/end',
      method: HttpMethod.POST,
      description: 'End an ownership assignment (exclusive end date; today = immediately)'
    },
    ACCOUNT_OWNER_DELETE: {
      path: '/account-owners/:ownerId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete an ownership assignment'
    },
    LP_ALLOCATION: {
      path: '/lp-allocation',
      method: HttpMethod.POST,
      description: 'LP Allocation grid: every LP-role account with its owners by role'
    },
    LP_ALLOCATION_FILTER_OPTIONS: {
      path: '/lp-allocation/filter-options',
      method: HttpMethod.POST,
      description: 'Dropdown options for the LP Allocation grid filters'
    },
    LP_ALLOCATION_BULK_AGENT_DRAFTS: {
      path: '/lp-allocation/bulk/agent-drafts',
      method: HttpMethod.POST,
      description: 'Bulk: agent drafts Default / On / Off for a selection or every LP matching the grid filters (admin)'
    },
    LP_ALLOCATION_BULK_OWNER: {
      path: '/lp-allocation/bulk/owner',
      method: HttpMethod.POST,
      description: 'Bulk: assign one person to one ownership role on a selection or every LP matching the grid filters (admin)'
    },
    RELATIONSHIP_FACTS: {
      path: '/accounts/:accountId/relationship-facts',
      method: HttpMethod.GET,
      description: 'Relationship facts ledger for an account (query: prospect_id, status, include_history)'
    },
    RELATIONSHIP_FACT_SAVE: {
      path: '/relationship-facts',
      method: HttpMethod.POST,
      description: 'Add or edit a relationship fact (a text change supersedes the old row)'
    },
    RELATIONSHIP_FACT_REVIEW: {
      path: '/relationship-facts/:factId/review',
      method: HttpMethod.POST,
      description: 'Accept or reject an agent-proposed fact'
    },
    RELATIONSHIP_FACT_ARCHIVE: {
      path: '/relationship-facts/:factId/archive',
      method: HttpMethod.POST,
      description: 'Archive a fact'
    },
    RELATIONSHIP_FACT_CONFIRM: {
      path: '/relationship-facts/:factId/confirm',
      method: HttpMethod.POST,
      description: 'Confirm a fact is still true (touches last_confirmed_at)'
    },
    ACCOUNT_FUND_EXPOSURE: {
      path: '/accounts/:accountId/fund-exposure',
      method: HttpMethod.GET,
      description: 'See-through exposure of an LP parent across its funds and consolidated funds'
    },
    CONFIG_ACCOUNT_OWNER_ROLES: {
      path: '/config/account-owner-roles',
      method: HttpMethod.GET,
      description: 'Configuration → Account Owner Roles (admin): list; POST creates; PUT/DELETE /:id'
    },
    CONFIG_RELATIONSHIP_FACT_TYPES: {
      path: '/config/relationship-fact-types',
      method: HttpMethod.GET,
      description: 'Configuration → Relationship Fact Types (admin): list; POST creates; PUT/DELETE /:id'
    }
  },
  DOCUMENT_REPORTS: {
    PREVIEW: {
      path: '/document-reports/preview',
      method: HttpMethod.POST,
      description: 'Render a document report to self-contained HTML for preview'
    },
    PDF: {
      path: '/document-reports/pdf',
      method: HttpMethod.POST,
      description: 'Render a document report to PDF'
    },
    CUSTOMIZATION_CONTEXT: {
      path: '/document-reports/customization-context',
      method: HttpMethod.POST,
      description: 'Generated content, pick pool and saved edits for the Customize drawer'
    },
    CUSTOMIZATIONS: {
      path: '/document-reports/customizations',
      method: HttpMethod.PUT,
      description: 'Save (or clear) a document report customization'
    }
  },

  // ============================================================================
  // UTILITIES ROUTES
  // ============================================================================
  UTILITIES: {
    JSON_TO_EXCEL: {
      path: '/utilities/json-to-excel',
      method: HttpMethod.POST,
      description: 'Convert JSON to Excel spreadsheet'
    },
    JSON_TO_EXCEL_BRANDED: {
      path: '/utilities/json-to-excel-branded',
      method: HttpMethod.POST,
      description: 'Convert JSON to an Excel spreadsheet in the report design system'
    },
    GLOBAL_SEARCH: {
      path: '/utilities/search',
      method: HttpMethod.GET,
      description: 'Global search across funds, consolidated funds, accounts, investors, deals, contacts'
    }
  },

  // ============================================================================
  // FILES ROUTES
  // ============================================================================
  FILES: {
    UPLOAD: { path: '/files/upload', method: HttpMethod.POST, description: 'Upload file to storage' },
    DELETE: { path: '/files/delete', method: HttpMethod.DELETE, description: 'Delete file from storage' },
    GET_SIGNED_URL: { path: '/files/signed-url', method: HttpMethod.GET, description: 'Get signed URL for a file' },
    LIST: { path: '/files/list', method: HttpMethod.GET, description: 'List files for a deal or folder' }
  },

  // ============================================================================
  // DISTRIBUTION LISTS ROUTES
  // ============================================================================
  // ----------------------------------------------------------------------
  // CONFIGURATION ROUTES (in-app management of cfg-schema config data)
  // ----------------------------------------------------------------------
  CONFIG: {
    GL_ACCOUNTS_ADMIN: {
      path: '/config/gl-accounts',
      method: HttpMethod.GET,
      description: 'GL account catalogue + account types + the global item->account default mapping'
    },
    GL_ACCOUNT_SAVE: {
      path: '/config/gl-accounts',
      method: HttpMethod.POST,
      description: 'Create or edit a catalogue GL account (cfg.acc_gl_accounts)'
    },
    GL_ITEM_MAPPING_SET: {
      path: '/config/gl-accounts/item-mapping',
      method: HttpMethod.POST,
      description: 'Move the global default account for an accounting item (prospective only)'
    },
    CHART_TEMPLATES_LIST: {
      path: '/config/chart-templates',
      method: HttpMethod.GET,
      description: 'Chart templates with their account subset and item map'
    },
    CHART_TEMPLATE_SAVE: {
      path: '/config/chart-templates',
      method: HttpMethod.POST,
      description: 'Upsert a chart template (replaces its account set and item map)'
    },
    CHART_TEMPLATE_DELETE: {
      path: '/config/chart-templates/:templateId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete a chart template'
    },
    WATERFALL_TEMPLATES_LIST: {
      path: '/config/waterfall-templates',
      method: HttpMethod.GET,
      description: 'Distribution waterfall templates with their tiers and splits'
    },
    WATERFALL_TEMPLATE_SAVE: {
      path: '/config/waterfall-templates',
      method: HttpMethod.POST,
      description: 'Upsert a waterfall template (its tiers and splits are a replacement set)'
    },
    WATERFALL_TEMPLATE_DELETE: {
      path: '/config/waterfall-templates/:templateId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete a waterfall template (fund waterfalls made from it keep working)'
    },
    INDUSTRIES_LIST: {
      path: '/config/industries',
      method: HttpMethod.GET,
      description: 'List industries (Configuration screen, default sort)'
    },
    INDUSTRIES_FILTER: {
      path: '/config/industries/filter',
      method: HttpMethod.POST,
      description: 'Filtered/sorted/paginated industries grid (with usage_count)'
    },
    INDUSTRIES_CREATE: {
      path: '/config/industries',
      method: HttpMethod.POST,
      description: 'Create an industry (cfg.industries)'
    },
    INDUSTRIES_UPDATE: {
      path: '/config/industries/:industryId',
      method: HttpMethod.PUT,
      description: 'Rename an industry'
    },
    INDUSTRIES_DELETE: {
      path: '/config/industries/:industryId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete an unused industry (in-use returns 409 → use Replace)'
    },
    INDUSTRIES_REASSIGN: {
      path: '/config/industries/reassign',
      method: HttpMethod.POST,
      description: 'Reassign accounts from source industries to a target, optionally deleting sources'
    },
    INDUSTRY_ACCOUNTS: {
      path: '/config/industries/:industryId/accounts',
      method: HttpMethod.GET,
      description: 'Accounts categorised with an industry (detail screen), alphabetical with roles'
    },
    ACCOUNT_INDUSTRY_UPDATE: {
      path: '/config/account-industry/:accountId',
      method: HttpMethod.PUT,
      description: "Change a single account's industry"
    },
    PIPELINES_FILTER: {
      path: '/config/pipelines/filter',
      method: HttpMethod.POST,
      description: 'Filtered/sorted/paginated pipelines grid (stage + deal counts, thresholds)'
    },
    PIPELINES_CREATE: {
      path: '/config/pipelines',
      method: HttpMethod.POST,
      description: 'Create a pipeline (public.pipelines)'
    },
    PIPELINES_UPDATE: {
      path: '/config/pipelines/:pipelineId',
      method: HttpMethod.PUT,
      description: 'Update a pipeline (name/type/default/fund + days-in-pipeline thresholds)'
    },
    PIPELINES_DELETE: {
      path: '/config/pipelines/:pipelineId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete an empty pipeline (still has stages/deals returns 409)'
    },
    PIPELINE_STAGES: {
      path: '/config/pipelines/:pipelineId/stages',
      method: HttpMethod.GET,
      description: "A pipeline's stages (pipeline_config) with settings, thresholds and deal counts"
    },
    STAGE_LIBRARY: {
      path: '/config/stage-library',
      method: HttpMethod.GET,
      description: 'Shared stage library (public.pipeline_stages) with usage counts'
    },
    STAGES_CREATE: {
      path: '/config/stages',
      method: HttpMethod.POST,
      description: 'Create a library stage'
    },
    STAGES_UPDATE: {
      path: '/config/stages/:stageId',
      method: HttpMethod.PUT,
      description: 'Update a library stage (name/colour/type/decision status)'
    },
    STAGES_DELETE: {
      path: '/config/stages/:stageId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete an unused stage (in-use returns 409)'
    },
    PIPELINE_CONFIG_CREATE: {
      path: '/config/pipeline-config',
      method: HttpMethod.POST,
      description: 'Attach a stage to a pipeline (create pipeline_config row)'
    },
    PIPELINE_CONFIG_UPDATE: {
      path: '/config/pipeline-config/:configId',
      method: HttpMethod.PUT,
      description: 'Update per-stage settings, order, colour and days-in-stage thresholds'
    },
    // User rights (docs/USER_RIGHTS_DESIGN.md): the roles, and one capability's
    // per-record rules - the Pipelines panel's "Who can move" column.
    ROLES: {
      path: '/config/roles',
      method: HttpMethod.GET,
      description: 'The live roles (cfg.roles), the built-in Admin and Everyone included'
    },
    CAPABILITY_OVERRIDES: {
      path: '/config/capabilities/:capabilityKey/overrides',
      method: HttpMethod.GET,
      description: "A capability's default roles and its per-record overrides"
    },
    CAPABILITY_OVERRIDE_SET: {
      path: '/config/capabilities/:capabilityKey/overrides/:scopeId',
      method: HttpMethod.PUT,
      description: 'Set who may, for one record: role_ids null follows the default, [] is admins only'
    },
    // Configuration -> Users & Roles (docs/USER_RIGHTS_DESIGN.md section 7)
    USER_RIGHTS_ROLES: {
      path: '/config/user-rights/roles',
      method: HttpMethod.GET,
      description: 'The roles with their members and how many rules name each (admin)'
    },
    USER_RIGHTS_USERS: {
      path: '/config/user-rights/users',
      method: HttpMethod.GET,
      description: 'The people who may sign in or hold a role, with the roles they hold (admin)'
    },
    USER_RIGHTS_USER_OPTIONS: {
      path: '/config/user-rights/user-options',
      method: HttpMethod.GET,
      description: 'Every live user, for the Members picker (admin)'
    },
    USER_RIGHTS_CAPABILITIES: {
      path: '/config/user-rights/capabilities',
      method: HttpMethod.GET,
      description: "The rights matrix: each capability's default roles and override count (admin)"
    },
    USER_RIGHTS_ROLE_CREATE: {
      path: '/config/user-rights/roles',
      method: HttpMethod.POST,
      description: 'Create a role: name, description, sort order (admin)'
    },
    USER_RIGHTS_ROLE_UPDATE: {
      path: '/config/user-rights/roles/:roleId',
      method: HttpMethod.PUT,
      description: 'Rename, describe or reorder a role (admin)'
    },
    USER_RIGHTS_ROLE_DELETE: {
      path: '/config/user-rights/roles/:roleId',
      method: HttpMethod.DELETE,
      description: 'Remove a role no rule names, with its memberships (admin)'
    },
    USER_RIGHTS_ROLE_MEMBER_ADD: {
      path: '/config/user-rights/roles/:roleId/members/:systemUserId',
      method: HttpMethod.PUT,
      description: 'Add a user to a role (admin)'
    },
    USER_RIGHTS_ROLE_MEMBER_REMOVE: {
      path: '/config/user-rights/roles/:roleId/members/:systemUserId',
      method: HttpMethod.DELETE,
      description: 'Take a user off a role; Admin keeps at least one member (admin)'
    },
    USER_RIGHTS_CAPABILITY_DEFAULT_SET: {
      path: '/config/user-rights/capabilities/:capabilityKey/default',
      method: HttpMethod.PUT,
      description: 'Set who may do something by default: role_ids, [] is admins only (admin)'
    },
    PIPELINE_CONFIG_DELETE: {
      path: '/config/pipeline-config/:configId',
      method: HttpMethod.DELETE,
      description: 'Remove a stage from a pipeline (still holds deals returns 409)'
    },
    STAGE_ACTIONS_LIST: {
      path: '/config/stage-actions',
      method: HttpMethod.GET,
      description: 'Configured stage actions (cfg.stage_actions) with pipeline/stage/type/template names + recipients'
    },
    STAGE_ACTION_TYPES: {
      path: '/config/stage-action-types',
      method: HttpMethod.GET,
      description: 'Stage-action type registry (cfg.stage_action_types)'
    },
    STAGE_ACTIONS_CREATE: {
      path: '/config/stage-actions',
      method: HttpMethod.POST,
      description: 'Create a stage action (+ recipient rows in the same atomic batch)'
    },
    STAGE_ACTIONS_UPDATE: {
      path: '/config/stage-actions/:stageActionId',
      method: HttpMethod.PUT,
      description: 'Update a stage action; recipient_user_ids (when present) replaces the recipient set'
    },
    STAGE_ACTIONS_DELETE: {
      path: '/config/stage-actions/:stageActionId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete a stage action and its recipient rows'
    },
    DEAL_ORIGINATION_EXCLUSIONS_LIST: {
      path: '/config/deal-origination-exclusions',
      method: HttpMethod.GET,
      description: 'Deal types with whether they are excluded from Deal Origination'
    },
    DEAL_ORIGINATION_EXCLUSIONS_SET: {
      path: '/config/deal-origination-exclusions',
      method: HttpMethod.PUT,
      description: 'Exclude or re-admit a deal type to Deal Origination'
    },
    DEAL_EXECUTION_MAPPING_LIST: {
      path: '/config/deal-execution-mapping',
      method: HttpMethod.GET,
      description: 'Deal types with their mapped execution pipeline'
    },
    DEAL_EXECUTION_MAPPING_SET: {
      path: '/config/deal-execution-mapping',
      method: HttpMethod.PUT,
      description: 'Set or clear a deal type’s execution pipeline'
    },
    STAGE_CHECKLIST_LIST: {
      path: '/config/stage-checklist-items',
      method: HttpMethod.GET,
      description: 'Checklist items across the execution pipelines'
    },
    STAGE_CHECKLIST_CREATE: {
      path: '/config/stage-checklist-items',
      method: HttpMethod.POST,
      description: 'Create a checklist item on a pipeline stage'
    },
    STAGE_CHECKLIST_UPDATE: {
      path: '/config/stage-checklist-items/:itemId',
      method: HttpMethod.PUT,
      description: 'Update a checklist item (label / sequence / active)'
    },
    STAGE_CHECKLIST_DELETE: {
      path: '/config/stage-checklist-items/:itemId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete a checklist item'
    },
    DOCUMENT_CONFIG_LIST: {
      path: '/config/document-config',
      method: HttpMethod.GET,
      description: 'Deal-document categories + types + requirements (one admin read)'
    },
    DOCUMENT_CATEGORY_CREATE: {
      path: '/config/document-categories',
      method: HttpMethod.POST,
      description: 'Create a document category (cfg.document_categories)'
    },
    DOCUMENT_CATEGORY_UPDATE: {
      path: '/config/document-categories/:categoryId',
      method: HttpMethod.PUT,
      description: 'Update a document category'
    },
    DOCUMENT_CATEGORY_DELETE: {
      path: '/config/document-categories/:categoryId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete an empty document category (in-use returns 409)'
    },
    DOCUMENT_TYPE_CREATE: {
      path: '/config/document-types',
      method: HttpMethod.POST,
      description: 'Create a document type (cfg.document_types)'
    },
    DOCUMENT_TYPE_UPDATE: {
      path: '/config/document-types/:typeId',
      method: HttpMethod.PUT,
      description: 'Update a document type (label, category, order, active)'
    },
    DOCUMENT_TYPE_DELETE: {
      path: '/config/document-types/:typeId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete an unused document type (in-use returns 409)'
    },
    DOCUMENT_REQUIREMENT_CREATE: {
      path: '/config/document-requirements',
      method: HttpMethod.POST,
      description: 'Create a required-document rule (cfg.deal_document_requirements)'
    },
    DOCUMENT_REQUIREMENT_UPDATE: {
      path: '/config/document-requirements/:requirementId',
      method: HttpMethod.PUT,
      description: 'Update a required-document rule (scope, lifecycle, satisfied-by)'
    },
    DOCUMENT_REQUIREMENT_DELETE: {
      path: '/config/document-requirements/:requirementId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete a required-document rule'
    },

    // Configuration → Internal Agents. Budget caps, task→model routing and the
    // model registry. Config and usage are separate reads: config is refetched
    // after every save, usage changes continuously.
    INTERACTION_TRACKING_CONFIG: {
      path: '/config/interaction-tracking',
      method: HttpMethod.GET,
      description: 'Tracked mailboxes (mail + calendar) and per-mailbox label overrides (admin)'
    },
    INTERACTION_TRACKING_MAILBOX_ADD: {
      path: '/config/interaction-tracking/mailboxes',
      method: HttpMethod.POST,
      description: 'Start tracking a mailbox'
    },
    INTERACTION_TRACKING_MAILBOX_UPDATE: {
      path: '/config/interaction-tracking/mailboxes/:mailboxId',
      method: HttpMethod.PUT,
      description: 'Update a tracked mailbox (mail / calendar / categorisation switches)'
    },
    INTERACTION_TRACKING_MAILBOX_REMOVE: {
      path: '/config/interaction-tracking/mailboxes/:mailboxId',
      method: HttpMethod.DELETE,
      description: 'Stop tracking a mailbox'
    },
    INTERACTION_TRACKING_MAILBOX_LABEL: {
      path: '/config/interaction-tracking/mailboxes/:mailboxId/labels',
      method: HttpMethod.PUT,
      description: 'Set a per-mailbox Outlook label/colour override for one category'
    },
    EMAIL_CATEGORIES_CONFIG: {
      path: '/config/email-categories',
      method: HttpMethod.GET,
      description: 'Email category taxonomy + categorization settings (admin)'
    },
    EMAIL_CATEGORY_UPDATE: {
      path: '/config/email-categories/:categoryId',
      method: HttpMethod.PUT,
      description: 'Update one email category (label, precedence, Outlook name/colour)'
    },
    EMAIL_CATEGORIES_REORDER: {
      path: '/config/email-categories/reorder',
      method: HttpMethod.POST,
      description: 'Rewrite the whole email-category precedence order'
    },
    EMAIL_CATEGORY_SETTING_UPDATE: {
      path: '/config/email-categories/settings/:configKey',
      method: HttpMethod.PUT,
      description: 'Set one categorization setting (cfg.app_config group email_categorization)'
    },
    EMAIL_RECIPIENT_MARKER_UPDATE: {
      path: '/config/email-categories/recipient-markers/:markerKey',
      method: HttpMethod.PUT,
      description: 'Update the To / Cc marker stamped alongside the content category'
    },
    STATEMENT_TYPES_CONFIG: {
      path: '/config/statement-types',
      method: HttpMethod.GET,
      description: 'The investor statement types a run produces and the report each prints, with the reports each may print instead (admin)'
    },
    STATEMENT_TYPE_UPDATE: {
      path: '/config/statement-types/:typeId',
      method: HttpMethod.PUT,
      description: 'Update one investor statement type: label, short label, display order or the document report it prints (admin)'
    },
    PIPELINE_BUDGET_ACCOUNTS_CONFIG: {
      path: '/config/pipeline-budget-accounts',
      method: HttpMethod.GET,
      description: 'The GL accounts the Deal Pipeline Budget tab lists under Cash, with the GL accounts a row can point at (admin)'
    },
    PIPELINE_BUDGET_ACCOUNT_CREATE: {
      path: '/config/pipeline-budget-accounts',
      method: HttpMethod.POST,
      description: 'Add a GL account to the Deal Pipeline Budget tab (admin)'
    },
    PIPELINE_BUDGET_ACCOUNT_UPDATE: {
      path: '/config/pipeline-budget-accounts/:accountId',
      method: HttpMethod.PUT,
      description: 'Update one Budget tab account: GL account, label or display order (admin)'
    },
    PIPELINE_BUDGET_ACCOUNT_DELETE: {
      path: '/config/pipeline-budget-accounts/:accountId',
      method: HttpMethod.DELETE,
      description: 'Remove a GL account from the Deal Pipeline Budget tab (admin)'
    },
    PIPELINE_STATUSES_LIST: {
      path: '/config/pipeline-statuses',
      method: HttpMethod.GET,
      description: 'Pipeline decision statuses with usage counts (cfg.pipeline_statuses)'
    },
    PIPELINE_STATUS_CREATE: {
      path: '/config/pipeline-statuses',
      method: HttpMethod.POST,
      description: 'Create a pipeline status (category fixed at creation)'
    },
    PIPELINE_STATUS_UPDATE: {
      path: '/config/pipeline-statuses/:statusId',
      method: HttpMethod.PUT,
      description: 'Rename / reorder a pipeline status'
    },
    PIPELINE_STATUS_DELETE: {
      path: '/config/pipeline-statuses/:statusId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete an unused pipeline status (409 when in use → Replace flow)'
    },
    PIPELINE_STATUSES_REASSIGN: {
      path: '/config/pipeline-statuses/reassign',
      method: HttpMethod.POST,
      description: 'Repoint every reference off source statuses onto a target, then soft-delete the sources'
    },
    FUND_STATUSES_LIST: {
      path: '/config/fund-statuses',
      method: HttpMethod.GET,
      description: 'Fund lifecycle statuses with usage counts (cfg.fund_statuses)'
    },
    FUND_STATUS_CREATE: {
      path: '/config/fund-statuses',
      method: HttpMethod.POST,
      description: 'Create a fund status (category and behaviour flags fixed at creation)'
    },
    FUND_STATUS_UPDATE: {
      path: '/config/fund-statuses/:statusId',
      method: HttpMethod.PUT,
      description: 'Rename / reorder a fund status'
    },
    FUND_STATUS_DELETE: {
      path: '/config/fund-statuses/:statusId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete an unused fund status (409 when in use → Replace flow)'
    },
    FUND_STATUSES_REASSIGN: {
      path: '/config/fund-statuses/reassign',
      method: HttpMethod.POST,
      description: 'Repoint every fund off source statuses onto a target, then soft-delete the sources'
    },
    LOOKUP_LISTS: {
      path: '/config/lookup-lists',
      method: HttpMethod.GET,
      description: 'Lookup headers still managed in lookup_details, with live value counts'
    },
    LOOKUP_LIST_DETAILS: {
      path: '/config/lookup-lists/:headerId/details',
      method: HttpMethod.GET,
      description: 'Values of one lookup list (trashed included — the disable toggle)'
    },
    LOOKUP_DETAIL_CREATE: {
      path: '/config/lookup-details',
      method: HttpMethod.POST,
      description: 'Add a value to a lookup list'
    },
    LOOKUP_DETAIL_UPDATE: {
      path: '/config/lookup-details/:detailId',
      method: HttpMethod.PUT,
      description: 'Rename / disable / re-enable a lookup value'
    },
    LOOKUP_DETAILS_REORDER: {
      path: '/config/lookup-details/reorder',
      method: HttpMethod.POST,
      description: 'Whole-order swap of one lookup list (single call)'
    },
    CONTACT_RELATIONSHIP_TYPES_LIST: {
      path: '/config/contact-relationships/types',
      method: HttpMethod.GET,
      description: 'Contact relationship types with titles/usage counts (cfg.contact_relationship_types)'
    },
    CONTACT_RELATIONSHIP_TYPE_CREATE: {
      path: '/config/contact-relationships/types',
      method: HttpMethod.POST,
      description: 'Create a contact relationship type'
    },
    CONTACT_RELATIONSHIP_TYPE_UPDATE: {
      path: '/config/contact-relationships/types/:typeId',
      method: HttpMethod.PUT,
      description: 'Rename / flag a contact relationship type (protected rows refuse renames)'
    },
    CONTACT_RELATIONSHIP_TYPE_DELETE: {
      path: '/config/contact-relationships/types/:typeId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete a titleless, unused contact relationship type (409 otherwise)'
    },
    CONTACT_RELATIONSHIP_TITLES_LIST: {
      path: '/config/contact-relationships/types/:typeId/titles',
      method: HttpMethod.GET,
      description: 'Titles of one relationship type with per-junction usage counts'
    },
    CONTACT_RELATIONSHIP_TITLE_CREATE: {
      path: '/config/contact-relationships/titles',
      method: HttpMethod.POST,
      description: 'Add a title under a relationship type (type immutable afterwards)'
    },
    CONTACT_RELATIONSHIP_TITLE_UPDATE: {
      path: '/config/contact-relationships/titles/:titleId',
      method: HttpMethod.PUT,
      description: 'Rename a contact relationship title (protected rows refuse renames)'
    },
    CONTACT_RELATIONSHIP_TITLE_DELETE: {
      path: '/config/contact-relationships/titles/:titleId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete an unused contact relationship title (409 when in use → Replace flow)'
    },
    CONTACT_RELATIONSHIP_TITLES_REASSIGN: {
      path: '/config/contact-relationships/titles/reassign',
      method: HttpMethod.POST,
      description: 'Repoint every junction reference off source titles onto a same-type target, then soft-delete the sources'
    },
    INTERNAL_AGENTS_CONFIG: {
      path: '/config/internal-agents',
      method: HttpMethod.GET,
      description: 'Internal-agent budget knobs, agent types, task routing, model registry'
    },
    INTERNAL_AGENTS_USAGE: {
      path: '/config/internal-agents/usage',
      method: HttpMethod.GET,
      description: 'Internal-agent spend and run counts since UTC midnight'
    },
    INTERNAL_AGENTS_APP_CONFIG_UPDATE: {
      path: '/config/internal-agents/app-config/:configKey',
      method: HttpMethod.PUT,
      description: 'Set one global internal-agent budget knob (cfg.app_config)'
    },
    INTERNAL_AGENT_TYPE_UPDATE: {
      path: '/config/internal-agents/agent-types/:agentTypeId',
      method: HttpMethod.PUT,
      description: 'Per-agent-type budget caps and kill switch (cfg.internal_agent_types)'
    },
    INTERNAL_AGENT_LP_STATUS_CADENCE_UPDATE: {
      path: '/config/internal-agents/lp-status-cadence/:statusId',
      method: HttpMethod.PUT,
      description: 'Cadence policy of one LP status: touchpoint threshold, draft mode, priority (cfg.comms_lp_statuses)'
    },
    INTERNAL_AGENT_TASK_MODEL_CREATE: {
      path: '/config/internal-agents/task-models',
      method: HttpMethod.POST,
      description: 'Route a task to a model (cfg.internal_agent_task_models)'
    },
    INTERNAL_AGENT_TASK_MODEL_UPDATE: {
      path: '/config/internal-agents/task-models/:taskModelId',
      method: HttpMethod.PUT,
      description: 'Update a task→model routing row'
    },
    INTERNAL_AGENT_TASK_MODEL_DELETE: {
      path: '/config/internal-agents/task-models/:taskModelId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete a task→model routing row'
    },
    LLM_MODEL_CREATE: {
      path: '/config/llm-models',
      method: HttpMethod.POST,
      description: 'Add a model to the registry (cfg.llm_models)'
    },
    LLM_MODEL_UPDATE: {
      path: '/config/llm-models/:modelId',
      method: HttpMethod.PUT,
      description: 'Update a model. Disabling one still routed returns 409'
    },
    LLM_MODEL_DELETE: {
      path: '/config/llm-models/:modelId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete a model. Refused with 409 while still routed'
    },

    // Configuration → Agent Gateway. Per-product volume caps and per-tool
    // value_caps for the MCP gateway.
    AGENT_GATEWAY_CONFIG: {
      path: '/config/agent-gateway',
      method: HttpMethod.GET,
      description: 'Agent products, tools and the cap whitelist'
    },
    AGENT_GATEWAY_USAGE: {
      path: '/config/agent-gateway/usage',
      method: HttpMethod.GET,
      description: 'Requests and rows consumed per product and per grant'
    },
    AGENT_GATEWAY_PRODUCT_UPDATE: {
      path: '/config/agent-gateway/products/:agentKey',
      method: HttpMethod.PUT,
      description: 'Per-product rate + daily row caps and kill switch (cfg.agents)'
    },
    AGENT_GATEWAY_TOOL_UPDATE: {
      path: '/config/agent-gateway/tools/:toolKey',
      method: HttpMethod.PUT,
      description: 'Per-tool enable + value_caps bag (cfg.agent_tools)'
    },
    AGENT_ACCESS_ADMIN: {
      path: '/config/agent-access',
      method: HttpMethod.GET,
      description: 'All users’ agent grants, usage vs caps, and recent activity (admin, view-only)'
    },
    AGENT_ACCESS_USAGE: {
      path: '/config/agent-access/usage',
      method: HttpMethod.GET,
      description: 'Agent usage over a period of UTC days: totals, by day, by connection, by tool (admin)'
    },
    AGENT_ACCESS_PROPOSALS: {
      path: '/config/agent-access/proposals',
      method: HttpMethod.GET,
      description: 'Agent proposals across users, filtered and windowed — list envelope (admin)'
    },
    AGENT_ACCESS_CALLS: {
      path: '/config/agent-access/calls',
      method: HttpMethod.GET,
      description: 'Every MCP tool call the agent gateway handled, refusals included — list envelope (admin)'
    },
    AGENT_ACCESS_CALL_INSIGHTS: {
      path: '/config/agent-access/call-insights',
      method: HttpMethod.GET,
      description: 'Tool health, failures, argument usage, empty searches and scope refusals over a period (admin)'
    }
  },

  DISTRIBUTION_LISTS: {
    TOPICS_LIST: {
      path: '/distribution-lists/topics',
      method: HttpMethod.GET,
      description: 'List comms topics (Topic Management grid, default sort)'
    },
    TOPICS_FILTER: {
      path: '/distribution-lists/topics/filter',
      method: HttpMethod.POST,
      description: 'Filtered/sorted/paginated comms topics grid'
    },
    TOPICS_CREATE: {
      path: '/distribution-lists/topics',
      method: HttpMethod.POST,
      description: 'Create a comms topic + reconcile its account-role mappings'
    },
    TOPICS_UPDATE: {
      path: '/distribution-lists/topics/:topicId',
      method: HttpMethod.PUT,
      description: 'Update a comms topic + reconcile its account-role mappings'
    },
    TOPICS_DELETE: {
      path: '/distribution-lists/topics/:topicId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete a comms topic and its account-role mappings'
    },
    ACCOUNT_TOPICS_GET: {
      path: '/distribution-lists/account-topics/:accountId',
      method: HttpMethod.GET,
      description: "One account's topic-subscription matrix (Account Topic tab)"
    },
    ACCOUNT_TOPICS_SAVE: {
      path: '/distribution-lists/account-topics/:accountId',
      method: HttpMethod.PUT,
      description: "Reconcile one account's account_topics + per-contact contact_account_topics"
    },
    BULK_ACCOUNT_TOPICS_FILTER: {
      path: '/distribution-lists/bulk-account-topics/filter',
      method: HttpMethod.POST,
      description: 'Filtered, account-paginated multi-account topic matrix (Bulk Topic Subscription Manager)'
    },
    BULK_ACCOUNT_TOPICS_FILTER_OPTIONS: {
      path: '/distribution-lists/bulk-account-topics/filter-options',
      method: HttpMethod.GET,
      description: 'Role / fund / LP-status option universes for the bulk manager filter bar'
    },
    BULK_ACCOUNT_TOPICS_SAVE: {
      path: '/distribution-lists/bulk-account-topics',
      method: HttpMethod.PUT,
      description: "Reconcile the changed accounts' topic matrices in one atomic batch"
    },
    LISTS_LIST: {
      path: '/distribution-lists/lists',
      method: HttpMethod.GET,
      description: 'List distribution lists (Definition grid, default sort)'
    },
    LISTS_FILTER: {
      path: '/distribution-lists/lists/filter',
      method: HttpMethod.POST,
      description: 'Filtered/sorted/paginated distribution lists grid'
    },
    LISTS_FILTER_OPTIONS: {
      path: '/distribution-lists/lists/filter-options',
      method: HttpMethod.GET,
      description: 'Topic / role / fund / LP-status option universes for the Definition modal'
    },
    LISTS_CREATE: {
      path: '/distribution-lists/lists',
      method: HttpMethod.POST,
      description: 'Create a distribution list + reconcile its role/fund/LP-status filters'
    },
    LISTS_UPDATE: {
      path: '/distribution-lists/lists/:listId',
      method: HttpMethod.PUT,
      description: 'Update a distribution list + reconcile its role/fund/LP-status filters'
    },
    LISTS_DELETE: {
      path: '/distribution-lists/lists/:listId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete a distribution list and its filter join rows'
    },
    LISTS_DETAIL: {
      path: '/distribution-lists/lists/:listId',
      method: HttpMethod.GET,
      description: 'One distribution list (full-form cold-load detail)'
    },
    USAGE_RESOLVE: {
      path: '/distribution-lists/usage/resolve',
      method: HttpMethod.POST,
      description:
        'Resolve a (possibly amended) distribution list to its recipient set (Send Communication)'
    }
  },

  // ============================================================================
  // CHANGE LOG ROUTES  (Change Log tab on entity full-forms)
  // ============================================================================
  CHANGE_LOG: {
    LIST: {
      path: '/change-log/:contextTable/:recordId',
      method: HttpMethod.POST,
      description: "Paginated change-log feed for one record (own fields + rolled-up child rows)"
    }
  },

  // ============================================================================
  // EMAIL ROUTES  (Phase 2: compose / draft full-form + signature + delegations)
  // ============================================================================
  EMAIL: {
    CAMPAIGNS_LIST: {
      path: '/email/campaigns',
      method: HttpMethod.GET,
      description: 'List email campaigns (default sort)'
    },
    CAMPAIGNS_FILTER: {
      path: '/email/campaigns/filter',
      method: HttpMethod.POST,
      description: 'Filtered/sorted/paginated email campaigns grid'
    },
    CAMPAIGNS_FILTER_OPTIONS: {
      path: '/email/campaigns/filter-options',
      method: HttpMethod.GET,
      description: 'Multiselect option universes for the Email Log audit grid (Phase 5)'
    },
    CAMPAIGNS_DETAIL: {
      path: '/email/campaigns/:campaignId',
      method: HttpMethod.GET,
      description: 'One email campaign (full-form cold-load detail, includes body_html + attachments)'
    },
    CAMPAIGNS_CREATE: {
      path: '/email/campaigns',
      method: HttpMethod.POST,
      description: 'Create an email campaign draft (sanitized body_html)'
    },
    CAMPAIGNS_UPDATE: {
      path: '/email/campaigns/:campaignId',
      method: HttpMethod.PUT,
      description: 'Update an email campaign draft (sanitized body_html)'
    },
    CAMPAIGNS_DELETE: {
      path: '/email/campaigns/:campaignId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete an email campaign'
    },
    ME_ELIGIBLE_SENDERS: {
      path: '/email/me/eligible-senders',
      method: HttpMethod.GET,
      description: 'Operator plus non-trashed grantors — backs the Send-as picker'
    },
    SYSTEM_USERS_SEARCH: {
      path: '/email/system-users/search',
      method: HttpMethod.POST,
      description: 'Free-text system_users search for the delegation grant picker'
    },
    ME_SIGNATURE_GET: {
      path: '/email/me/signature',
      method: HttpMethod.GET,
      description: 'My signature (raw override + resolved final HTML for preview)'
    },
    ME_SIGNATURE_SAVE: {
      path: '/email/me/signature',
      method: HttpMethod.PATCH,
      description: 'Save my signature override (NULL/empty = revert to firm default)'
    },
    SIGNATURE_RESOLVE: {
      path: '/email/signatures/resolve',
      method: HttpMethod.POST,
      description: 'Resolve signature HTML for a given sent_as_user_id (Send-as picker change)'
    },
    DELEGATIONS_LIST: {
      path: '/email/delegations',
      method: HttpMethod.GET,
      description: 'My delegations (granted_to_me + granted_by_me)'
    },
    DELEGATIONS_GRANT: {
      path: '/email/delegations',
      method: HttpMethod.POST,
      description: 'Grant a delegation (caller must be the grantor — non-repudiation)'
    },
    DELEGATIONS_REVOKE: {
      path: '/email/delegations/:delegationId',
      method: HttpMethod.DELETE,
      description: 'Revoke a delegation (soft-delete; caller must be the grantor)'
    },
    // ── Phase 3: recipient resolution + staging + override ─────────────────
    RECIPIENTS_RESOLVE: {
      path: '/email/campaigns/:campaignId/recipients/resolve',
      method: HttpMethod.POST,
      description:
        'Resolve a (possibly amended) distribution-list definition to its recipient set for a campaign'
    },
    RECIPIENTS_FILTER: {
      path: '/email/campaigns/:campaignId/recipients/filter',
      method: HttpMethod.POST,
      description: 'Filtered/sorted/paginated staged recipients grid for a campaign'
    },
    RECIPIENTS_STAGE: {
      path: '/email/campaigns/:campaignId/recipients',
      method: HttpMethod.PUT,
      description:
        'Replace the staged recipient set under a draft campaign (transactional)'
    },
    RECIPIENT_OVERRIDE_SAVE: {
      path: '/email/recipients/:recipientId/override',
      method: HttpMethod.PUT,
      description:
        'Save per-recipient override (subject, body, role); sanitized server-side'
    },
    // ── Phase 4: queue + dispatch retry ─────────────────────────────────────
    CAMPAIGNS_QUEUE: {
      path: '/email/campaigns/:campaignId/queue',
      method: HttpMethod.POST,
      description:
        'Queue a draft campaign (delegation + domain + size pre-checks + fire-and-forget dispatch invoke)'
    },
    CAMPAIGNS_DISPATCH_RETRY: {
      path: '/email/campaigns/:campaignId/dispatch-retry',
      method: HttpMethod.POST,
      description:
        'Re-invoke the dispatch edge function for an already-queued campaign (idempotent — already-sent skipped)'
    },
    CAMPAIGNS_CANCEL: {
      path: '/email/campaigns/:campaignId/cancel',
      method: HttpMethod.POST,
      description:
        'Cancel a queued campaign before dispatch. Refuses sending (race) — idempotent on cancelled.'
    },
    CAMPAIGNS_HALT: {
      path: '/email/campaigns/:campaignId/halt',
      method: HttpMethod.POST,
      description:
        'Halt a sending campaign — the dispatch stops at its next batch and returns unsent recipients to queued. Idempotent on halted.'
    },
    CAMPAIGNS_RESUME: {
      path: '/email/campaigns/:campaignId/resume',
      method: HttpMethod.POST,
      description:
        'Resume a halted campaign (re-invokes the dispatch). Refused while recipients are still unconfirmed (sending).'
    },
    CAMPAIGNS_RECONCILE: {
      path: '/email/campaigns/:campaignId/reconcile',
      method: HttpMethod.POST,
      description:
        'Check a halted/failed campaign\'s unconfirmed recipients against Postmark: accepted → sent, not found → re-queued. Sends nothing.'
    },
    CAMPAIGNS_APPLY_TEMPLATE_ATTACHMENTS: {
      path: '/email/campaigns/:campaignId/template-attachments',
      method: HttpMethod.POST,
      description:
        "Copy a design template's attachments onto a draft campaign (Storage copy-by-value; dedup by file_name)"
    },
    // ── Track D1 (ship plan): test send modal ───────────────────────────────
    TEST_RECIPIENTS_GET: {
      path: '/email/test-recipients',
      method: HttpMethod.GET,
      description:
        'Curated default recipients (cfg.app_config email/test_recipients) for the compose Test Send modal'
    },
    CAMPAIGNS_TEST_SEND: {
      path: '/email/campaigns/:campaignId/test-send',
      method: HttpMethod.POST,
      description:
        "Test-send a draft campaign to one or more chosen addresses ([TEST] subject prefix; no campaign state mutated)"
    },
    // ── Phase 4b: team-mode UX ─────────────────────────────────────────────
    TEAM_OVERRIDE_SAVE: {
      path: '/email/campaigns/:campaignId/teams/:accountId/override',
      method: HttpMethod.PUT,
      description:
        'Per-team subject/body override — applied to every email_recipients row of the (campaign, account)'
    },
    TEAM_RECIPIENT_ADD: {
      path: '/email/campaigns/:campaignId/teams/:accountId/recipients',
      method: HttpMethod.POST,
      description:
        'Add one contact (already on the account) to a team. Body: {data:{contact_id, role}}'
    },
    TEAM_CANDIDATE_SEARCH: {
      path: '/email/campaigns/:campaignId/teams/:accountId/candidates',
      method: HttpMethod.POST,
      description:
        'Search contacts on the account that aren’t already in the team. Body: {data:{search_term?}}'
    },
    RECIPIENT_REMOVE: {
      path: '/email/recipients/:recipientId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete one staged recipient (Phase 4b team detail Remove)'
    },
    // Campaign-level Cc / Bcc copy recipients (added to every message a
    // campaign sends; distinct from the primary recipient list).
    COPIES_LIST: {
      path: '/email/campaigns/:campaignId/copies',
      method: HttpMethod.GET,
      description: 'List a campaign’s Cc/Bcc copy recipients → {cc:[], bcc:[]}'
    },
    COPY_ADD: {
      path: '/email/campaigns/:campaignId/copies',
      method: HttpMethod.POST,
      description: 'Add one Cc/Bcc copy. Body: {data:{kind, contact_id?, email_address?}}'
    },
    COPY_REMOVE: {
      path: '/email/copies/:copyId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete one Cc/Bcc copy recipient'
    },
    // Attachments that belong to one account each (capital call notices,
    // stage 5). Read-only on the compose form.
    CAMPAIGNS_ACCOUNT_FILES: {
      path: '/email/campaigns/:campaignId/account-files',
      method: HttpMethod.GET,
      description:
        'A campaign’s per-account attachments → {data:[one row per account that is staged or holds a file], prog__summary}'
    },
    CHANNELS_GET: {
      path: '/email/channels',
      method: HttpMethod.GET,
      description:
        'Send type options for the compose form (cfg) and the state of the mailbox channel → {channels:[{code,label,description,enabled,disabled_reason}], mailbox:{enabled,provider_key,sender_emails}}'
    },
    CAMPAIGNS_REQUEUE_FAILED: {
      path: '/email/campaigns/:campaignId/requeue-failed',
      method: HttpMethod.POST,
      description:
        'Put a failed mailbox campaign’s failed recipients (the ones that never left the mailbox) back in the queue. Sends nothing.'
    }
  },

  // ============================================================================
  // EMAIL DESIGN TEMPLATES (2026-05-29 — Templates tab inside the Email module)
  // ============================================================================
  EMAIL_DESIGN_TEMPLATES: {
    FILTER: {
      path: '/email-design-templates/filter',
      method: HttpMethod.POST,
      description: 'Filtered / sorted / paginated email design templates grid'
    },
    FILTER_OPTIONS: {
      path: '/email-design-templates/filter-options',
      method: HttpMethod.GET,
      description: 'Multiselect option universes for the templates grid'
    },
    DETAIL: {
      path: '/email-design-templates/:templateId',
      method: HttpMethod.GET,
      description: 'One template (full-form cold-load detail; includes body_html)'
    },
    CREATE: {
      path: '/email-design-templates',
      method: HttpMethod.POST,
      description: 'Create a template (body_html sanitized server-side when body_source=jodit)'
    },
    UPDATE: {
      path: '/email-design-templates/:templateId',
      method: HttpMethod.PUT,
      description: 'Update a template (body_html sanitized server-side when body_source=jodit)'
    },
    DELETE: {
      path: '/email-design-templates/:templateId',
      method: HttpMethod.DELETE,
      description: 'Soft-delete a template (trashed=true)'
    }
  }
} as const

/**
 * Type for all available route keys
 */
type RouteKey = keyof (typeof API_ROUTES)[keyof typeof API_ROUTES]

/**
 * Helper function to build full URL with base URL
 * @param baseUrl - Base URL (e.g., from environment variable)
 * @param route - Route definition
 * @returns Full URL string
 */
function buildUrl(baseUrl: string, route: RouteDefinition): string {
  return `${baseUrl}${route.path}`
}

/**
 * Helper function to build URL with parameters
 * @param baseUrl - Base URL (e.g., from environment variable)
 * @param route - Route definition
 * @param params - Parameters to replace in the path (e.g., { id: '123', dealId: '456' })
 * @returns Full URL string with parameters replaced
 */
function buildUrlWithParams(baseUrl: string, route: RouteDefinition, params: Record<string, string | number>): string {
  let path = route.path

  // Replace path parameters
  Object.entries(params).forEach(([key, value]) => {
    path = path.replace(`:${key}`, String(value))
  })

  return `${baseUrl}${path}`
}

/**
 * Helper function to build URL with query parameters
 * @param baseUrl - Base URL (e.g., from environment variable)
 * @param route - Route definition
 * @param queryParams - Query parameters to append
 * @returns Full URL string with query parameters
 */
function buildUrlWithQuery(
  baseUrl: string,
  route: RouteDefinition,
  queryParams: Record<string, string | number | boolean>
): string {
  const url = buildUrl(baseUrl, route)
  const searchParams = new URLSearchParams()

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.append(key, String(value))
    }
  })

  const queryString = searchParams.toString()
  return queryString ? `${url}?${queryString}` : url
}

/**
 * Helper function to build URL with both path parameters and query parameters
 * @param baseUrl - Base URL (e.g., from environment variable)
 * @param route - Route definition
 * @param pathParams - Path parameters to replace in the path
 * @param queryParams - Query parameters to append
 * @returns Full URL string with both path and query parameters
 */
export function buildFullUrl(
  baseUrl: string,
  route: RouteDefinition,
  pathParams: Record<string, string | number> = {},
  queryParams: Record<string, string | number | boolean> = {}
): string {
  let path = route.path

  // Replace path parameters
  Object.entries(pathParams).forEach(([key, value]) => {
    path = path.replace(`:${key}`, String(value))
  })

  const url = `${baseUrl}${path}`

  // Add query parameters
  const searchParams = new URLSearchParams()
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.append(key, String(value))
    }
  })

  const queryString = searchParams.toString()
  return queryString ? `${url}?${queryString}` : url
}

/**
 * Get the backend base URL from environment variables
 * @returns Backend base URL
 */
export function getBackendBaseUrl(): string {
  return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'
}

/**
 * Convenience function to get a route by key
 * @param module - Module name (e.g., 'COMPANIES')
 * @param action - Action name (e.g., 'LIST')
 * @returns Route definition
 */
function getRoute(module: keyof typeof API_ROUTES, action: keyof (typeof API_ROUTES)[typeof module]): RouteDefinition {
  return API_ROUTES[module][action] as RouteDefinition
}
